import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Info } from "lucide-react";
import ReactDOM from "react-dom";
import { calculateCPFRatioFromStrings } from "./CPFCalculator";
import { BASE_URL } from "../../api/url";
import {
  getMealPlanTypeList,
  addMealPlanDetails,
  getMealPlanCount,
  getGlobalTemplateDetails,
  getAssignedClientMealPlanPreview,
  getGlobalTemplatesSuggest,
  getMealPlanSuggest,
  getMealPlanFilterTemplate,
  getMealPlanFilterAssignedClient,
  getClientListForMealPlan,
  duplicateMealPlan,
  duplicateMealPlanTemplate,
} from "../../../services/mealPlans";
import { createGlobalTemplate } from "../../../services/globalTemplates";
import { getAvailableNutritionistsList } from "../../../services/sales";
import {
  createAdhocMealPlanFromScratch,
  createAdhocMealPlanFromTemplate,
  createAdhocMealPlanFromExistingClientPlan,
  duplicateAdhocMealPlan,
  getAdhocMealPlanCategories,
} from "../../../services/adhocMealPlans";
import { useAuth } from "../../../context/auth/AuthContext";
import useCustomScrollbar from "../../styles/useCustomScrollbar";
import SharedDropdown, { DropdownOption } from "../../Ui_Components/CustomDropdown";
import UserDetailSection from "../../client_details/component/User_Details_Section/UserDetailSection";
import PageHeader from "../../Ui_Components/PageHeader";
import { ToastContainer, useToast } from "../../Ui_Components/ToastMessage";
import { Button } from "../../Ui_Components/Button";
import Pagination from "../../Ui_Components/Pagination";
import MealPlanStepIndicator, { StepIndicatorMode } from "./components/MealPlanStepIndicator";
import ClientContactSection, { ClientContactData, isClientContactComplete } from "./components/ClientContactSection";
import { ReactComponent as DeleteIcon } from "../../../assects/svg/client_details/delete.svg";
import { ReactComponent as UseTempletIcon } from "../../../assects/svg/client_details/meal_plan/Use_a_template.svg";
import { ReactComponent as UseTempletActiveIcon } from "../../../assects/svg/client_details/meal_plan/Use_a_template_active.svg";
import { ReactComponent as CopyClientIcon } from "../../../assects/svg/client_details/meal_plan/Copy_from_a_client.svg";
import { ReactComponent as CopyClientActiveIcon } from "../../../assects/svg/client_details/meal_plan/Copy_from_a_client_active.svg";
import { ReactComponent as FromScratchIcon } from "../../../assects/svg/client_details/meal_plan/Start_from_scratch.svg";
import { ReactComponent as FromScratchActiveIcon } from "../../../assects/svg/client_details/meal_plan/Start_from_scratch_active.svg";
import { ReactComponent as RecommendationIcon } from "../../../assects/svg/client_details/meal_plan/recommendation-inactive.svg";
import { ReactComponent as RecommendationActiveIcon } from "../../../assects/svg/client_details/meal_plan/recommendation-active.svg";
import { ReactComponent as FilterIcon } from "../../../assects/svg/client_details/meal_plan/filter-inactive.svg";
import { ReactComponent as FilterActiveIcon } from "../../../assects/svg/client_details/meal_plan/filter-active.svg";


const toOptions = (strs: string[]): DropdownOption[] => strs.map((s) => ({ value: s, label: s }));

interface MealTiming {
  id: string;
  mealType: string;
  time: { hour: string; minute: string; period: string };
  fk_user_meal?: number;
}

interface Step1Data {
  planType: string;
  totalCalories: { min: string; max: string };
  totalAdjProtein: { min: string; max: string };
  totalNetCarbs: { min: string; max: string };
  totalFats: { min: string; max: string };
  totalGL: { min: string; max: string };
  cpfRatio: {
    min: { c: string; p: string; f: string };
    max: { c: string; p: string; f: string };
  };
  instructions: string;
}

interface TemplateIngredient {
  name: string;
  quantity: number | string;
  unit: string;
}

interface TemplateMealOption {
  name: string;
  ingredients: TemplateIngredient[];
  nutrientCount: number;
}

interface TemplateMealSlot {
  mealType: string;
  timeStr: string;
  hour: string;
  minute: string;
  period: string;
  options: TemplateMealOption[];
}

interface MealTemplate {
  id: string;
  goal: string;
  planType: string;
  matchPercent: number;
  mealsPerDay: number;
  optionsPerMeal: number;
  creatorName: string;
  creatorInitial: string;
  creatorColor: string;
  creatorPhoto?: string;
  cal: { min: number; max: number };
  adjProtein: { min: number; max: number };
  carbs: { min: number; max: number };
  fats: { min: number; max: number };
  gl: { min: number; max: number };
  mealSlots: TemplateMealSlot[];
}

interface ClientPlan extends MealTemplate {
  clientName: string;
}

interface ApiTemplateItem {
  template_id: number;
  template_code?: string;
  template_name?: string;
  category_name?: string;
  meal_plan_type: string;
  template_goal?: string;
  visibility?: string;
  total_calories_min?: string;
  total_calories_max?: string;
  total_protein_min?: string;
  total_protein_max?: string;
  total_carbs_min?: string;
  total_carbs_max?: string;
  total_fats_min?: string;
  total_fats_max?: string;
  total_GL_min?: string;
  total_GL_max?: string;
  created_by_name?: string;
  meals_per_day?: number;
  avg_options_per_meal?: number;
  match_percent?: number;
  goal_matched?: boolean;
  nutritionist_profile?: string;
  nutritionist_photo?: string;
}

interface ApiClientPlanItem {
  meal_plan_id: number;
  plan_code?: string;
  meal_plan_name?: string;
  plan_goal?: string;
  meal_plan_type: string;
  total_calories_min?: string;
  total_calories_max?: string;
  total_protein_min?: string;
  total_protein_max?: string;
  total_carbs_min?: string;
  total_carbs_max?: string;
  total_fats_min?: string;
  total_fats_max?: string;
  total_GL_min?: string;
  total_GL_max?: string;
  client_name?: string;
  nutritionist_name?: string;
  meals_per_day?: number;
  avg_options_per_meal?: number;
  match_percent?: number;
  goal_matched?: boolean;
  nutritionist_profile?: string | null;
  nutritionist_photo?: string | null;
}

const CREATOR_COLORS = ["#FF6B6B", "#34C759", "#007AFF", "#AF52DE", "#FF9F0A", "#FF3B30", "#0DB5B5"];

const mapApiClientPlan = (p: ApiClientPlanItem): ClientPlan => {
  const name = p.nutritionist_name ?? "";
  const color = CREATOR_COLORS[p.meal_plan_id % CREATOR_COLORS.length];
  return {
    id: String(p.meal_plan_id),
    goal: p.plan_goal || p.meal_plan_name || p.plan_code || "",
    planType: p.meal_plan_type,
    matchPercent: p.match_percent ?? 0,
    mealsPerDay: p.meals_per_day ?? 0,
    optionsPerMeal: p.avg_options_per_meal ?? 0,
    creatorName: name,
    creatorInitial: name[0] ?? "?",
    creatorColor: color,
    creatorPhoto: p.nutritionist_photo || p.nutritionist_profile || "",
    cal: { min: parseFloat(p.total_calories_min ?? "0"), max: parseFloat(p.total_calories_max ?? "0") },
    adjProtein: { min: parseFloat(p.total_protein_min ?? "0"), max: parseFloat(p.total_protein_max ?? "0") },
    carbs: { min: parseFloat(p.total_carbs_min ?? "0"), max: parseFloat(p.total_carbs_max ?? "0") },
    fats: { min: parseFloat(p.total_fats_min ?? "0"), max: parseFloat(p.total_fats_max ?? "0") },
    gl: { min: parseFloat(p.total_GL_min ?? "0"), max: parseFloat(p.total_GL_max ?? "0") },
    mealSlots: [],
    clientName: p.client_name ?? "",
  };
};

const mapApiTemplate = (t: ApiTemplateItem): MealTemplate => {
  const name = t.created_by_name ?? "";
  const color = CREATOR_COLORS[t.template_id % CREATOR_COLORS.length];
  return {
    id: String(t.template_id),
    goal: t.template_goal || t.category_name || t.template_name || t.template_code || "",
    planType: t.meal_plan_type,
    matchPercent: t.match_percent ?? 0,
    mealsPerDay: t.meals_per_day ?? 0,
    optionsPerMeal: t.avg_options_per_meal ?? 0,
    creatorName: name,
    creatorInitial: name[0] ?? "?",
    creatorColor: color,
    creatorPhoto: t.nutritionist_photo || t.nutritionist_profile || "",
    cal: { min: parseFloat(t.total_calories_min ?? "0"), max: parseFloat(t.total_calories_max ?? "0") },
    adjProtein: { min: parseFloat(t.total_protein_min ?? "0"), max: parseFloat(t.total_protein_max ?? "0") },
    carbs: { min: parseFloat(t.total_carbs_min ?? "0"), max: parseFloat(t.total_carbs_max ?? "0") },
    fats: { min: parseFloat(t.total_fats_min ?? "0"), max: parseFloat(t.total_fats_max ?? "0") },
    gl: { min: parseFloat(t.total_GL_min ?? "0"), max: parseFloat(t.total_GL_max ?? "0") },
    mealSlots: [],
  };
};

interface ApiTemplateFood {
  food_id?: number;
  meal_food_id?: number;
  fk_food_id?: number | null;
  fk_dish_id?: number;
  fk_dish__dish_name?: string;
  food_name?: string;
  quantity?: string | number;
  units?: string;
  cals?: string;
  carbs?: string;
  AAFAdj_protein?: string;
  total_EAA?: string;
  GL?: string;
  oil?: string;
  hidden_fat?: string;
  gross_protein?: string;
  potassium?: string;
  lysine?: string;
  tdf?: string;
  AA_factor?: string;
  measure_unit_type?: string;
  measure_unit_id?: number;
}

interface ApiTemplateOption {
  option_id?: number;
  option_no?: number;
  option_name: string;
  option_instructions?: string;
  total_cals?: string;
  total_carbs?: string;
  total_fat?: string;
  total_gl?: string;
  total_oil?: string;
  total_aadjprotein?: string;
  foods?: ApiTemplateFood[];
}

interface ApiTemplateMeal {
  meal_id?: number;
  meal_type_name: string;
  hour?: number;
  minute?: number;
  period?: string;
  is_custom?: boolean;
  options?: ApiTemplateOption[];
}

interface ApiTemplateDetail {
  template_id: number;
  template_code?: string;
  template_name?: string;
  description?: string;
  category?: string | null;
  meal_plan_type: string;
  meals_per_day?: number;
  total_calories_min?: string;
  total_calories_max?: string;
  total_protein_min?: string;
  total_protein_max?: string;
  total_carbs_min?: string;
  total_carbs_max?: string;
  total_fats_min?: string;
  total_fats_max?: string;
  total_GL_min?: string;
  total_GL_max?: string;
  CPF_min?: string;
  CPF_max?: string;
  instructions?: string;
  visibility?: string;
  created_by_name?: string;
  nutritionist_name?: string;
  nutritionist_profile?: string | null;
  meals?: ApiTemplateMeal[];
}

interface ApiClientPlanDetail {
  meal_plan_id: number;
  plan_code?: string;
  meal_plan_name?: string;
  meal_plan_type: string;
  instructions?: string;
  total_calories_min?: string;
  total_calories_max?: string;
  total_calories_units?: string;
  total_protein_min?: string;
  total_protein_max?: string;
  total_protein_units?: string;
  total_carbs_min?: string;
  total_carbs_max?: string;
  total_carbs_units?: string;
  total_fats_min?: string;
  total_fats_max?: string;
  total_fats_units?: string;
  total_GL_min?: string;
  total_GL_max?: string;
  total_GL_units?: string;
  CPF_min?: string;
  CPF_max?: string;
  client_name?: string;
  nutritionist_name?: string;
  nutritionist_profile?: string | null;
  meals?: ApiTemplateMeal[];
}

const padTwo = (n: number) => String(n).padStart(2, "0");

const mealToMinutes = (m: ApiTemplateMeal): number => {
  const h = m.hour ?? 0;
  const min = m.minute ?? 0;
  const isPM = (m.period ?? "AM").toUpperCase() === "PM";
  const hours24 = isPM ? (h === 12 ? 12 : h + 12) : (h === 12 ? 0 : h);
  return hours24 * 60 + min;
};

const buildMealSlots = (meals: ApiTemplateMeal[]): TemplateMealSlot[] =>
  [...meals].sort((a, b) => mealToMinutes(a) - mealToMinutes(b)).map((m) => {
    const h = m.hour ?? 0;
    const min = m.minute ?? 0;
    const period = (m.period ?? "AM").toUpperCase();
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const timeStr = `${padTwo(hour12)}:${padTwo(min)} ${period}`;
    const options: TemplateMealOption[] = (m.options ?? []).map((opt) => ({
      name: opt.option_name,
      nutrientCount: parseFloat(opt.total_cals ?? "0"),
      ingredients: (opt.foods ?? []).map((f) => ({
        name: f.fk_dish__dish_name ?? f.food_name ?? "Food item",
        quantity: f.quantity ?? 0,
        unit: f.units ?? "g",
      })),
    }));
    return { mealType: m.meal_type_name, timeStr, hour: padTwo(hour12), minute: padTwo(min), period, options };
  });

const mapApiTemplateDetail = (d: ApiTemplateDetail, base: MealTemplate): MealTemplate => {
  const mealSlots = buildMealSlots(d.meals ?? []);
  const name = d.nutritionist_name ?? d.created_by_name ?? base.creatorName;
  return {
    ...base,
    creatorName: name,
    creatorInitial: name[0] ?? base.creatorInitial,
    creatorPhoto: d.nutritionist_profile ?? base.creatorPhoto ?? "",
    mealsPerDay: d.meals_per_day ?? mealSlots.length,
    cal: { min: parseFloat(d.total_calories_min ?? "0"), max: parseFloat(d.total_calories_max ?? "0") },
    adjProtein: { min: parseFloat(d.total_protein_min ?? "0"), max: parseFloat(d.total_protein_max ?? "0") },
    carbs: { min: parseFloat(d.total_carbs_min ?? "0"), max: parseFloat(d.total_carbs_max ?? "0") },
    fats: { min: parseFloat(d.total_fats_min ?? "0"), max: parseFloat(d.total_fats_max ?? "0") },
    gl: { min: parseFloat(d.total_GL_min ?? "0"), max: parseFloat(d.total_GL_max ?? "0") },
    mealSlots,
  };
};

const mapApiClientPlanDetail = (d: ApiClientPlanDetail, base: ClientPlan): ClientPlan => {
  const mealSlots = buildMealSlots(d.meals ?? []);
  const name = d.nutritionist_name ?? base.creatorName;
  return {
    ...base,
    creatorName: name,
    creatorInitial: name[0] ?? base.creatorInitial,
    creatorPhoto: d.nutritionist_profile ?? base.creatorPhoto ?? "",
    mealsPerDay: mealSlots.length,
    cal: { min: parseFloat(d.total_calories_min ?? "0"), max: parseFloat(d.total_calories_max ?? "0") },
    adjProtein: { min: parseFloat(d.total_protein_min ?? "0"), max: parseFloat(d.total_protein_max ?? "0") },
    carbs: { min: parseFloat(d.total_carbs_min ?? "0"), max: parseFloat(d.total_carbs_max ?? "0") },
    fats: { min: parseFloat(d.total_fats_min ?? "0"), max: parseFloat(d.total_fats_max ?? "0") },
    gl: { min: parseFloat(d.total_GL_min ?? "0"), max: parseFloat(d.total_GL_max ?? "0") },
    mealSlots,
    clientName: d.client_name ?? base.clientName,
  };
};

interface MealCardProps {
  meal: MealTiming;
  isSelected: boolean;
  showMenu: boolean;
  onSelect: () => void;
  onDelete: () => void;
  formatTime: (hour: string, minute: string, period: string) => string;
}

const MealCard: React.FC<MealCardProps> = ({
  meal,
  isSelected,
  showMenu,
  onSelect,
  onDelete,
  formatTime,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (showDropdown && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom - 10;
      const dropdownHeight = 96;
      const top =
        spaceBelow >= dropdownHeight
          ? buttonRect.bottom + 4
          : buttonRect.top - dropdownHeight - 4;
      setDropdownPosition({
        top,
        left: buttonRect.right - 128,
        width: 128,
        maxHeight: dropdownHeight,
      });
    }
  }, [showDropdown]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDropdown]);

  return (
    <div
      onClick={onSelect}
      className={`p-3 border-[0.5px] rounded-md cursor-pointer transition-all bg-white ${
        isSelected
          ? "border-[#0DB5B5] bg-white"
          : "border-[#E0E0E0] hover:border-[#0DB5B5]"
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h4 className="font-medium text-[#1F1F1F] text-sm 3xl:text-base mb-1">
            {meal.mealType}
          </h4>
          <p className="text-[#8E8E93] text-sm 3xl:text-base">
            {formatTime(meal.time.hour, meal.time.minute, meal.time.period)}
          </p>
        </div>
        {showMenu && (
          <div className="relative">
            <button
              ref={buttonRef}
              onClick={(e) => {
                e.stopPropagation();
                setShowDropdown(!showDropdown);
              }}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <svg className="w-4 h-4 text-[#646464]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
              </svg>
            </button>
            {showDropdown && dropdownPosition &&
              ReactDOM.createPortal(
                <div
                  ref={dropdownRef}
                  style={{ position: "fixed", top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px`, width: `${dropdownPosition.width}px`, zIndex: 9999 }}
                  className="bg-white border border-[#E0E0E0] rounded-md shadow-lg p-2"
                >
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowDropdown(false); onSelect(); }}
                    className="w-full px-4 py-2 text-left text-sm 3xl:text-base text-[#1F1F1F] hover:bg-[#F5F5F5] transition-colors flex items-center gap-2 rounded-md"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowDropdown(false); onDelete(); }}
                    className="w-full px-4 py-2 text-left text-sm 3xl:text-base text-[#FF3B30] hover:bg-[#FFF5F5] transition-colors flex items-center gap-2 rounded-md"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </button>
                </div>,
                document.body,
              )}
          </div>
        )}
      </div>
    </div>
  );
};

interface FilterDropdownProps {
  value: string | string[];
  placeholder: string;
  options: string[];
  onChange: (val: string | string[]) => void;
  multiSelect?: boolean;
  searchable?: boolean;
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  value,
  placeholder,
  options,
  onChange,
  multiSelect = false,
  searchable = false,
}) => {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [dropdownPosition, setDropdownPosition] = React.useState<{
    top: number; left: number; width: number; maxHeight: number;
  } | null>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  const selectedValues: string[] = multiSelect
    ? (value as string[])
    : (value as string) ? [(value as string)] : [];
  const hasSelection = selectedValues.length > 0;

  React.useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  React.useEffect(() => {
    if (open && buttonRef.current) {
      const r = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom - 10;
      const spaceAbove = r.top - 10;
      const maxH = 240;
      let top: number;
      let maxHeight: number;
      if (spaceBelow >= maxH || spaceBelow >= spaceAbove) {
        top = r.bottom + 4;
        maxHeight = Math.min(maxH, spaceBelow);
      } else {
        maxHeight = Math.min(maxH, spaceAbove);
        top = r.top - maxHeight - 4;
      }
      setDropdownPosition({ top, left: r.left, width: r.width, maxHeight });
    }
  }, [open]);

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(e.target as Node)
      ) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  React.useEffect(() => {
    const handler = (e: Event) => {
      if (dropdownRef.current?.contains(e.target as Node)) return;
      if (open) setOpen(false);
    };
    if (open) window.addEventListener("scroll", handler, true);
    return () => window.removeEventListener("scroll", handler, true);
  }, [open]);

  const handleOptionClick = (opt: string) => {
    if (multiSelect) {
      const vals = value as string[];
      onChange(vals.includes(opt) ? vals.filter((v) => v !== opt) : [...vals, opt]);
    } else {
      onChange((value as string) === opt ? "" : opt);
      setOpen(false);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(multiSelect ? [] : "");
    setOpen(false);
  };

  const allSelected = multiSelect && options.length > 0 && (value as string[]).length === options.length &&
    options.every((o) => (value as string[]).includes(o));

  const displayText = (() => {
    if (!hasSelection) return "";
    if (!multiSelect) return value as string;
    if (allSelected) return "All";
    const vals = value as string[];
    return vals.length > 0 ? vals[0] : "";
  })();

  return (
    <div className="relative w-full">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((p) => !p)}
        className={`w-full px-3 py-1 border-[0.5px] rounded-full text-xs 3xl:text-sm  font-inter bg-[#F9F9F9] flex items-center gap-1 focus:outline-none transition-colors ${hasSelection ? "border-[#0DB5B5]" : "border-[#D9D9D9]"}`}
      >
        <span className={`truncate flex-1 text-left text-xs 3xl:text-sm  ${displayText ? "text-[#202020]" : "text-[#9E9E9E]"}`}>
          {displayText || placeholder}
        </span>
        {multiSelect && !allSelected && selectedValues.length > 1 && (
          <span className="flex-shrink-0 text-xs font-semibold text-[#0DB5B5]">+{selectedValues.length - 1}</span>
        )}
        {hasSelection ? (
          <span onClick={handleClear} className="flex-shrink-0 text-[#9E9E9E] hover:text-[#FF3B30] transition-colors">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </span>
        ) : (
          <svg className={`w-4 h-4 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} fill="none" stroke="#9E9E9E" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </button>
      {open && dropdownPosition && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          style={{ position: "fixed", top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px`, minWidth: `${dropdownPosition.width}px`, width: "max-content", maxHeight: `${dropdownPosition.maxHeight}px`, zIndex: 9999 }}
          className="bg-white border border-[#E0E0E0] rounded-md shadow-lg overflow-hidden flex flex-col"
          onScroll={(e) => e.stopPropagation()}
        >
          {searchable && (
            <div className="p-2 border-b border-[#F0F0F0] flex-shrink-0">
              <div className="relative">
                <svg className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#8E8E93]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  autoFocus
                  type="text"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ border: "0.5px solid #D9D9D9" }}
                  className="w-full pl-7 pr-2 py-1.5 rounded-md text-xs bg-white focus:outline-none"
                />
              </div>
            </div>
          )}
          <div className="py-2 overflow-y-auto custom-scrollbar-overlay">
            {(searchable ? options.filter((o) => o.toLowerCase().includes(search.toLowerCase())) : options).map((opt) => {
              const isSelected = !allSelected && selectedValues.includes(opt);
              return (
                <div
                  key={opt}
                  onClick={() => handleOptionClick(opt)}
                  className={`px-3 py-2 mx-2 my-1 text-xs 3xl:text-sm  font-inter cursor-pointer rounded-md flex items-center gap-2 ${isSelected ? "bg-[#E6FBFB] text-[#0DB5B5]" : "text-[#202020] hover:bg-[#F5F5F5]"}`}
                >
                  <span>{opt}</span>
                </div>
              );
            })}
            {searchable && options.filter((o) => o.toLowerCase().includes(search.toLowerCase())).length === 0 && (
              <div className="px-3 py-2 mx-2 my-1 text-xs text-[#8E8E93]">No matches</div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const isCaloriesValueValid = (value: string): boolean => {
  if (value === "" || value === "-") return true;
  const digitsOnly = value.replace(/[^0-9]/g, "");
  return digitsOnly.length <= 4;
};

// Step indicator moved to ./components/MealPlanStepIndicator

const MEAL_OPTION_POOL: TemplateMealOption[] = [
  {
    name: "Raspberry + Aloe vera juice",
    ingredients: [
      { name: "Raspberry", quantity: 4.0, unit: "Cup(m)" },
      { name: "Aloe vera juice", quantity: 1, unit: "Glass(m)" },
      { name: "Honey", quantity: 1, unit: "Tsp" },
       { name: "Banana", quantity: 1, unit: "Medium" },
      { name: "Almond milk", quantity: 200, unit: "ml" },
    ],
    nutrientCount: 5,
  },
  {
    name: "Oats + Banana Smoothie",
    ingredients: [
      { name: "Rolled oats", quantity: 0.5, unit: "Cup" },
      { name: "Banana", quantity: 1, unit: "Medium" },
      { name: "Almond milk", quantity: 200, unit: "ml" },
    ],
    nutrientCount: 3,
  },
  {
    name: "Grilled Chicken Bowl",
    ingredients: [
      { name: "Chicken breast", quantity: 150, unit: "g" },
      { name: "Brown rice", quantity: 0.75, unit: "Cup" },
      { name: "Broccoli", quantity: 100, unit: "g" },
      { name: "Olive oil", quantity: 1, unit: "Tbsp" },
    ],
    nutrientCount: 5,
  },
  {
    name: "Moong Dal Khichdi",
    ingredients: [
      { name: "Moong dal", quantity: 0.5, unit: "Cup" },
      { name: "Rice", quantity: 0.5, unit: "Cup" },
      { name: "Ghee", quantity: 1, unit: "Tsp" },
      { name: "Cumin seeds", quantity: 0.5, unit: "Tsp" },
    ],
    nutrientCount: 4,
  },
  {
    name: "Greek Yogurt Parfait",
    ingredients: [
      { name: "Greek yogurt", quantity: 150, unit: "g" },
      { name: "Mixed berries", quantity: 80, unit: "g" },
      { name: "Granola", quantity: 30, unit: "g" },
    ],
    nutrientCount: 3,
  },
  {
    name: "Paneer Tikka Wrap",
    ingredients: [
      { name: "Paneer", quantity: 100, unit: "g" },
      { name: "Whole wheat roti", quantity: 2, unit: "Piece" },
      { name: "Bell pepper", quantity: 50, unit: "g" },
      { name: "Mint chutney", quantity: 1, unit: "Tbsp" },
    ],
    nutrientCount: 4,
  },
  {
    name: "Vegetable Quinoa Bowl",
    ingredients: [
      { name: "Quinoa", quantity: 0.5, unit: "Cup" },
      { name: "Spinach", quantity: 60, unit: "g" },
      { name: "Cherry tomatoes", quantity: 80, unit: "g" },
      { name: "Lemon juice", quantity: 1, unit: "Tbsp" },
    ],
    nutrientCount: 5,
  },
  {
    name: "Egg White Omelette",
    ingredients: [
      { name: "Egg whites", quantity: 4, unit: "Nos" },
      { name: "Mushrooms", quantity: 50, unit: "g" },
      { name: "Capsicum", quantity: 30, unit: "g" },
    ],
    nutrientCount: 3,
  },
  {
    name: "Coconut Water + Nuts",
    ingredients: [
      { name: "Coconut water", quantity: 250, unit: "ml" },
      { name: "Almonds", quantity: 10, unit: "Nos" },
      { name: "Walnuts", quantity: 5, unit: "Nos" },
    ],
    nutrientCount: 2,
  },
  {
    name: "Masoor Dal + Roti",
    ingredients: [
      { name: "Masoor dal", quantity: 1, unit: "Cup(m)" },
      { name: "Whole wheat roti", quantity: 2, unit: "Piece" },
      { name: "Curd", quantity: 100, unit: "g" },
      { name: "Salad", quantity: 1, unit: "Bowl" },
    ],
    nutrientCount: 5,
  },
];

const buildSlots = (
  slots: Array<{ mealType: string; timeStr: string; hour: string; minute: string; period: string; optCount: number }>,
): TemplateMealSlot[] =>
  slots.map((s) => ({
    mealType: s.mealType,
    timeStr: s.timeStr,
    hour: s.hour,
    minute: s.minute,
    period: s.period,
    options: Array.from({ length: s.optCount }, (_, i) => ({
      ...MEAL_OPTION_POOL[i % MEAL_OPTION_POOL.length],
      ingredients: [...MEAL_OPTION_POOL[i % MEAL_OPTION_POOL.length].ingredients],
    })),
  }));


const GLOBAL_TEMPLATES: MealTemplate[] = [
  {
    id: "tpl-1", goal: "Gut health", planType: "Regular", matchPercent: 95,
    mealsPerDay: 4, optionsPerMeal: 4, creatorName: "Priya M", creatorInitial: "P", creatorColor: "#FF6B6B",
    cal: { min: 1200, max: 1300 }, adjProtein: { min: 20, max: 30 }, carbs: { min: 155, max: 160 },
    fats: { min: 150, max: 160 }, gl: { min: 5, max: 12 },
    mealSlots: buildSlots([
      { mealType: "Breakfast", timeStr: "10:00 AM", hour: "10", minute: "00", period: "AM", optCount: 5 },
      { mealType: "Lunch", timeStr: "02:00 PM", hour: "02", minute: "00", period: "PM", optCount: 7 },
      { mealType: "Tea", timeStr: "05:00 PM", hour: "05", minute: "00", period: "PM", optCount: 6 },
      { mealType: "Dinner", timeStr: "08:00 PM", hour: "08", minute: "00", period: "PM", optCount: 8 },
      { mealType: "Bed Time", timeStr: "10:00 PM", hour: "10", minute: "00", period: "PM", optCount: 4 },
    ]),
  },
  {
    id: "tpl-2", goal: "Gut health", planType: "Detox", matchPercent: 86,
    mealsPerDay: 6, optionsPerMeal: 4, creatorName: "Deepika C", creatorInitial: "D", creatorColor: "#34C759",
    cal: { min: 1200, max: 1300 }, adjProtein: { min: 20, max: 30 }, carbs: { min: 155, max: 160 },
    fats: { min: 150, max: 160 }, gl: { min: 5, max: 12 },
    mealSlots: buildSlots([
      { mealType: "On Waking Up", timeStr: "07:00 AM", hour: "07", minute: "00", period: "AM", optCount: 3 },
      { mealType: "Breakfast", timeStr: "09:00 AM", hour: "09", minute: "00", period: "AM", optCount: 6 },
      { mealType: "Lunch", timeStr: "01:00 PM", hour: "01", minute: "00", period: "PM", optCount: 7 },
      { mealType: "Tea Time", timeStr: "04:00 PM", hour: "04", minute: "00", period: "PM", optCount: 5 },
      { mealType: "Dinner", timeStr: "07:00 PM", hour: "07", minute: "00", period: "PM", optCount: 6 },
      { mealType: "Bed Time", timeStr: "10:00 PM", hour: "10", minute: "00", period: "PM", optCount: 4 },
    ]),
  },
  {
    id: "tpl-3", goal: "Gut health", planType: "Travel", matchPercent: 75,
    mealsPerDay: 4, optionsPerMeal: 4, creatorName: "Priya M", creatorInitial: "P", creatorColor: "#FF6B6B",
    cal: { min: 1200, max: 1300 }, adjProtein: { min: 20, max: 30 }, carbs: { min: 155, max: 160 },
    fats: { min: 150, max: 160 }, gl: { min: 5, max: 12 },
    mealSlots: buildSlots([
      { mealType: "Breakfast", timeStr: "08:00 AM", hour: "08", minute: "00", period: "AM", optCount: 2 },
      { mealType: "Lunch", timeStr: "01:00 PM", hour: "01", minute: "00", period: "PM", optCount: 3 },
      { mealType: "Dinner", timeStr: "07:00 PM", hour: "07", minute: "00", period: "PM", optCount: 3 },
      { mealType: "Bed Time", timeStr: "10:00 PM", hour: "10", minute: "00", period: "PM", optCount: 1 },
    ]),
  },
  {
    id: "tpl-4", goal: "Gut health", planType: "Fitness performance", matchPercent: 52,
    mealsPerDay: 4, optionsPerMeal: 4, creatorName: "Anusha R", creatorInitial: "A", creatorColor: "#AF52DE",
    cal: { min: 1200, max: 1300 }, adjProtein: { min: 20, max: 30 }, carbs: { min: 155, max: 160 },
    fats: { min: 150, max: 160 }, gl: { min: 5, max: 12 },
    mealSlots: buildSlots([
      { mealType: "Pre Breakfast", timeStr: "06:00 AM", hour: "06", minute: "00", period: "AM", optCount: 1 },
      { mealType: "Breakfast", timeStr: "08:00 AM", hour: "08", minute: "00", period: "AM", optCount: 2 },
      { mealType: "Lunch", timeStr: "12:00 PM", hour: "12", minute: "00", period: "PM", optCount: 3 },
      { mealType: "Dinner", timeStr: "07:00 PM", hour: "07", minute: "00", period: "PM", optCount: 2 },
    ]),
  },
  {
    id: "tpl-5", goal: "Gut health", planType: "Regular veg", matchPercent: 52,
    mealsPerDay: 4, optionsPerMeal: 4, creatorName: "Anusha R", creatorInitial: "A", creatorColor: "#AF52DE",
    cal: { min: 1200, max: 1300 }, adjProtein: { min: 20, max: 30 }, carbs: { min: 155, max: 160 },
    fats: { min: 150, max: 160 }, gl: { min: 5, max: 12 },
    mealSlots: buildSlots([
      { mealType: "Breakfast", timeStr: "09:00 AM", hour: "09", minute: "00", period: "AM", optCount: 2 },
      { mealType: "Lunch", timeStr: "01:00 PM", hour: "01", minute: "00", period: "PM", optCount: 3 },
      { mealType: "Tea Time", timeStr: "04:30 PM", hour: "04", minute: "30", period: "PM", optCount: 2 },
      { mealType: "Dinner", timeStr: "08:00 PM", hour: "08", minute: "00", period: "PM", optCount: 2 },
    ]),
  },
  {
    id: "tpl-6", goal: "Gut health", planType: "Regular non-veg", matchPercent: 52,
    mealsPerDay: 4, optionsPerMeal: 4, creatorName: "Anusha R", creatorInitial: "A", creatorColor: "#AF52DE",
    cal: { min: 1200, max: 1300 }, adjProtein: { min: 20, max: 30 }, carbs: { min: 155, max: 160 },
    fats: { min: 150, max: 160 }, gl: { min: 5, max: 12 },
    mealSlots: buildSlots([
      { mealType: "Breakfast", timeStr: "09:00 AM", hour: "09", minute: "00", period: "AM", optCount: 2 },
      { mealType: "Lunch", timeStr: "01:00 PM", hour: "01", minute: "00", period: "PM", optCount: 3 },
      { mealType: "Tea Time", timeStr: "04:30 PM", hour: "04", minute: "30", period: "PM", optCount: 2 },
      { mealType: "Dinner", timeStr: "08:00 PM", hour: "08", minute: "00", period: "PM", optCount: 2 },
    ]),
  },
];


// ── Shared plan card ─────────────────────────────────────────────────────────
interface PlanCardProps {
  plan: MealTemplate;
  isTopMatch: boolean;
  isCreating: boolean;
  onPreview: () => void;
  onUse: () => void;
  showMatch?: boolean;
}

const PlanCard: React.FC<PlanCardProps> = ({ plan, isTopMatch, isCreating, onPreview, onUse, showMatch = true }) => (
  <div className="bg-white rounded-lg p-4 border border-[#E0E0E0] flex flex-col gap-2.5">
    <div className="flex flex-col gap-0.5">
      <div className="flex items-start justify-between gap-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-sm font-semibold text-[#1F1F1F]">{plan.goal}</span>
          <span className="text-[#D9D9D9] text-sm select-none">|</span>
          <span className={`text-sm font-medium ${plan.matchPercent >= 90 ? "text-[#0DB5B5]" : plan.matchPercent >= 75 ? "text-[#FF9F0A]" : "text-[#8E8E93]"}`}>
            {plan.planType}
          </span>
        </div>
        {showMatch && (
          <span
            className="px-2 py-0.5 text-[10px] rounded-full font-medium flex-shrink-0"
            style={{ backgroundColor: isTopMatch ? "#34C759" : "#F0F0F0", color: isTopMatch ? "#fff" : "#646464" }}
          >
            {plan.matchPercent}% match
          </span>
        )}
      </div>
      <p className="text-xs text-[#8E8E93]">{plan.mealsPerDay} meals/day · {plan.optionsPerMeal} options/meal</p>
    </div>

    <div className="flex items-center gap-1.5">
      {plan.creatorPhoto ? (
        <img
          src={`${BASE_URL}${plan.creatorPhoto}`}
          alt={plan.creatorName}
          className="w-5 h-5 rounded-full object-cover flex-shrink-0"
          onError={(e) => {
            const t = e.currentTarget;
            t.style.display = "none";
            const fb = t.nextElementSibling as HTMLElement | null;
            if (fb) fb.style.display = "flex";
          }}
        />
      ) : null}
      <div
        className="w-5 h-5 rounded-full items-center justify-center text-white text-[10px] font-semibold flex-shrink-0"
        style={{ backgroundColor: plan.creatorColor, display: plan.creatorPhoto ? "none" : "flex" }}
      >
        {plan.creatorInitial}
      </div>
      <span className="text-xs text-[#646464]">{plan.creatorName}</span>
    </div>

    <div className="border-t border-[#F0F0F0]" />

    <div className="grid grid-cols-3 gap-x-2 gap-y-1 text-xs">
      <span className="text-[#8E8E93]">Cal - <span className="text-[#FF6B6B] font-medium">{plan.cal.min}-{plan.cal.max}</span></span>
      <span className="text-[#8E8E93]">A.pro - <span className="text-[#4ECDC4] font-medium">{plan.adjProtein.min}-{plan.adjProtein.max}g</span></span>
      <span className="text-[#8E8E93]">Carb - <span className="text-[#FFD93D] font-medium">{plan.carbs.min}-{plan.carbs.max}g</span></span>
      <span className="text-[#8E8E93]">Fats - <span className="text-[#C084FC] font-medium">{plan.fats.min}-{plan.fats.max}g</span></span>
      <span className="text-[#8E8E93]">GL - <span className="text-[#84CC16] font-medium">{String(plan.gl.min).padStart(2, "0")}-{plan.gl.max}</span></span>
    </div>

    <div className="flex items-center gap-2 pt-1">
      <Button
        variant="secondary"
        size="xs"
        onClick={onPreview}
        className="flex-1"
      >
        Preview
      </Button>
      <Button
        variant="primary"
        size="xs"
        onClick={onUse}
        disabled={isCreating}
        className="flex-1"
      >
        Duplicate &amp; use
      </Button>
    </div>
  </div>
);

// ── Shared plan preview modal ─────────────────────────────────────────────────
interface PlanPreviewModalProps {
  plan: MealTemplate | null;
  actionLabel: string;
  isCreating: boolean;
  isLoadingPreview?: boolean;
  onClose: () => void;
  onUse: (plan: MealTemplate) => void;
}

const PlanPreviewModal: React.FC<PlanPreviewModalProps> = ({ plan, actionLabel, isCreating, isLoadingPreview, onClose, onUse }) => {
  if (isLoadingPreview) return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl p-10 flex flex-col items-center gap-3 shadow-2xl">
        <svg className="w-8 h-8 text-[#0DB5B5] animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
        <p className="text-sm text-[#8E8E93]">Loading preview…</p>
      </div>
    </div>,
    document.body
  );

  if (!plan) return null;

  const calcPct = (v: number, t: number) => (t > 0 ? Math.min((v / t) * 100, 100) : 0);
  const cpfMin = calculateCPFRatioFromStrings(
    String(plan.carbs.min), String(plan.adjProtein.min), String(plan.fats.min), String(plan.cal.min),
  );
  const cpfMax = calculateCPFRatioFromStrings(
    String(plan.carbs.max), String(plan.adjProtein.max), String(plan.fats.max), String(plan.cal.max),
  );
  const stats: Array<{ label: string; unit: string; color: string; min: number; max: number }> = [
    { label: "Cal", unit: "kcal", color: "#B44AE6", min: plan.cal.min, max: plan.cal.max },
    { label: "(A)Prot.", unit: "g", color: "#6BBDFA", min: plan.adjProtein.min, max: plan.adjProtein.max },
    { label: "Carb", unit: "g", color: "#FFB532", min: plan.carbs.min, max: plan.carbs.max },
    { label: "Fat", unit: "g", color: "#F25BEC", min: plan.fats.min, max: plan.fats.max },
    { label: "GL", unit: "", color: "#A0CA07", min: plan.gl.min, max: plan.gl.max },
  ];

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-[90vw] max-h-[90vh] flex flex-col shadow-2xl">

        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-[#1F1F1F]">{plan.goal}</h2>
            <span className="text-[#D9D9D9] text-sm select-none">|</span>
            <span className="text-base font-medium text-[#0DB5B5]">{plan.planType}</span>
          </div>
          <div className="flex items-center gap-2">
            {plan.creatorPhoto ? (
              <img
                src={`${BASE_URL}${plan.creatorPhoto}`}
                alt={plan.creatorName}
                className="w-5 h-5 rounded-full object-cover flex-shrink-0"
                onError={(e) => {
                  const t = e.currentTarget;
                  t.style.display = "none";
                  const fb = t.nextElementSibling as HTMLElement | null;
                  if (fb) fb.style.display = "flex";
                }}
              />
            ) : null}
            <div
              className="w-5 h-5 rounded-full items-center justify-center text-white text-[10px] font-semibold"
              style={{ backgroundColor: plan.creatorColor, display: plan.creatorPhoto ? "none" : "flex" }}
            >
              {plan.creatorInitial}
            </div>
            <span className="text-sm text-[#8E8E93]">{plan.creatorName}</span>
          </div>
        </div>

        {/* Nutrition bar */}
        <div className="px-6 py-4 border-t border-b border-[#E0E0E0] flex-shrink-0">
          <div className="grid grid-cols-6 gap-6 w-full">
            {stats.map((s) => (
              <div key={s.label}>
                <div className="flex justify-between mb-1">
                  <span className="text-[#1F1F1F] text-[12px] font-medium font-inter">
                    {s.label}{s.unit && <span className="text-[#8D8D8D]"> ({s.unit})</span>}
                  </span>
                  <span className="text-[#1F1F1F] text-[12px] font-medium font-inter">
                    {s.min}/{s.max}
                  </span>
                </div>
                <div className="w-full h-[3px] bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${calcPct(s.min, s.max)}%`, backgroundColor: s.color }} />
                </div>
              </div>
            ))}
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-[#1F1F1F] text-[12px] font-medium font-inter">C:P:F</span>
                <span className="text-[#1F1F1F] text-[12px] font-medium font-inter">
                  {cpfMin.ratio.c}:{cpfMin.ratio.p}:{cpfMin.ratio.f} / {cpfMax.ratio.c}:{cpfMax.ratio.p}:{cpfMax.ratio.f}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Meal columns */}
        <div className="flex-1 min-h-0 overflow-x-auto custom-scrollbar-overlay px-6 pt-3 pb-2">
          <div className="flex gap-4 h-full">
            {plan.mealSlots.map((slot, idx) => (
              <PreviewMealColumn key={idx} slot={slot} />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#E0E0E0] flex items-center justify-end gap-3 flex-shrink-0">
          <Button
            variant="secondary"
            size="md"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => onUse(plan)}
            disabled={isCreating}
          >
            {isCreating && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            {actionLabel}
          </Button>
        </div>

      </div>
    </div>,
    document.body,
  );
};

interface PreviewMealColumnProps {
  slot: TemplateMealSlot;
}

const PreviewMealColumn: React.FC<PreviewMealColumnProps> = ({ slot }) => {
  const [hasScrollbar, setHasScrollbar] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkScrollbar = () => {
      if (containerRef.current) {
        setHasScrollbar(
          containerRef.current.scrollHeight > containerRef.current.clientHeight,
        );
      }
    };
    checkScrollbar();
    window.addEventListener("resize", checkScrollbar);
    const observer = new MutationObserver(checkScrollbar);
    if (containerRef.current) {
      observer.observe(containerRef.current, { childList: true, subtree: true });
    }
    return () => {
      window.removeEventListener("resize", checkScrollbar);
      observer.disconnect();
    };
  }, [slot.options.length]);

  return (
    <div className="flex-shrink-0 w-[280px] bg-white border-[0.5px] border-[#E5E5EA] rounded-md flex flex-col">
      <div className="flex-shrink-0 flex items-start justify-between px-4 pt-4 pb-2">
        <div>
          <h4 className="text-[#1F1F1F] text-sm font-medium">{slot.mealType}</h4>
          <p className="text-[#8E8E93] text-[10px]">{slot.timeStr}</p>
        </div>
        <span className="text-[#1F1F1F] font-medium text-[12px] px-[6px] py-[1px] rounded-[6px] bg-[#EFEFEF] flex-shrink-0">
          {slot.options.length}
        </span>
      </div>

      <div
        ref={containerRef}
        className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-overlay px-4 pb-4 pt-1"
      >
        <div className={`flex flex-col gap-2${hasScrollbar ? " -mr-[8px]" : ""}`}>
          {slot.options.map((option, optIdx) => (
            <div key={optIdx} className="bg-white border-[0.5px] border-[#E5E5EA] rounded-md p-3 h-[132px] flex flex-col">
              <h5 className="flex-shrink-0 text-xs font-semibold text-[#1F1F1F] mb-1.5 leading-tight">
                {option.name}
              </h5>
              <div className="flex-1 min-h-0 card-inner-scrollbar space-y-1">
                {option.ingredients.map((ing, ingIdx) => (
                  <div key={ingIdx} className="flex items-center justify-between text-[11px]">
                    <span className="text-[#646464]">{ing.name}</span>
                    <span className="text-[#8E8E93] flex-shrink-0 pl-2">
                      {ing.quantity} {ing.unit}
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex-shrink-0 mt-1.5 pt-1.5 border-t border-[#F0F0F0] flex items-center justify-between">
                <span className="text-[11px] text-[#0DB5B5]">Nutrients</span>
                <span className="text-[11px] text-[#0DB5B5] font-medium">
                  {String(option.ingredients.length).padStart(2, "0")}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const MealPlanCreatePage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { toasts, removeToast, error: showError, warning } = useToast();
  useCustomScrollbar();

  const planName: string = (location.state as any)?.planName || "";
  const isGlobalTemplateMode: boolean = (location.state as any)?.globalTemplateMode === true;
  const isAdhocMealMode: boolean = (location.state as any)?.adhocMealMode === true;
  const isAdhocDuplicateMode: boolean =
    isAdhocMealMode && (location.state as any)?.duplicateMode === true;
  const duplicateSourcePlanId: number | null =
    (location.state as any)?.sourcePlanId ?? null;

  // In global template / adhoc mode there's no client userId in route params — use nutritionist's own ID
  const effectiveUserId =
    userId ??
    (isGlobalTemplateMode || isAdhocMealMode ? String(user?.nutrist_id ?? 0) : "0");
  const userIdNumber = parseInt(effectiveUserId, 10);

  const stepIndicatorMode: StepIndicatorMode = isGlobalTemplateMode
    ? "global"
    : isAdhocMealMode
      ? "adhoc"
      : "client";

  const [currentStep, setCurrentStep] = useState(1);
  const [step2Selection, setStep2Selection] = useState<"template" | "client" | "scratch" | null>(
    isGlobalTemplateMode ? "scratch" : null
  );

  // Global template mode — editable plan name + goal in step 1
  const [globalPlanName, setGlobalPlanName] = useState("");
  const [globalGoal, setGlobalGoal] = useState("");

  // Adhoc meal mode — client contact + plan name in step 1
  const [adhocContact, setAdhocContact] = useState<ClientContactData>({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    location: "",
  });
  const [adhocPlanName, setAdhocPlanName] = useState("");
  const [adhocCategories, setAdhocCategories] = useState<
    { id: number; name: string }[]
  >([]);
  const [isAdhocCategoriesLoading, setIsAdhocCategoriesLoading] = useState(false);
  const [adhocCategoryId, setAdhocCategoryId] = useState<number | null>(null);
  const GLOBAL_GOALS = [
    "Lose weight", "Gain weight", "Wellness tracking", "Belly Fat",
    "Energy and fitness", "Health conditions", "Women health", "Gut Health",
    "Cravings and Emotional Eating", "Mental Health", "Skin health",
  ];

  const [showInstructions, setShowInstructions] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});
  const [isCreating, setIsCreating] = useState(false);
  const [showDeleteInstructionsConfirm, setShowDeleteInstructionsConfirm] = useState(false);

  const [showPlanTypeDropdown, setShowPlanTypeDropdown] = useState(false);
  const [planTypeSearchTerm, setPlanTypeSearchTerm] = useState("");
  const planTypeDropdownRef = useRef<HTMLDivElement>(null);

  const [planTypes, setPlanTypes] = useState<string[]>([]);
  const [isPlanTypesLoading, setIsPlanTypesLoading] = useState(false);

  const [step1Data, setStep1Data] = useState<Step1Data>({
    planType: "",
    totalCalories: { min: "", max: "" },
    totalAdjProtein: { min: "", max: "" },
    totalNetCarbs: { min: "", max: "" },
    totalFats: { min: "", max: "" },
    totalGL: { min: "", max: "" },
    cpfRatio: {
      min: { c: "0", p: "0", f: "0" },
      max: { c: "0", p: "0", f: "0" },
    },
    instructions: "",
  });

  const [mealTimings, setMealTimings] = useState<MealTiming[]>([]);
  const [selectedMealId, setSelectedMealId] = useState<string | null>(null);
  const [currentMeal, setCurrentMeal] = useState({
    mealType: "",
    hour: "Hr",
    minute: "Min",
    period: "AM",
  });

  const allMealTypes = [
    "On Waking Up", "Pre Breakfast", "Breakfast", "Mid Morning", "Brunch",
    "Lunch", "Post Lunch", "Tea Time", "Dinner", "Post Dinner", "Snacks",
    "Bed Time", "Other",
  ];

  const availableMealTypes = allMealTypes.filter(
    (mealType) =>
      !mealTimings.some((meal) => meal.mealType === mealType && meal.id !== selectedMealId),
  );

  const hours = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));
  const minutes = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

  const filteredPlanTypes = planTypes.filter((type) =>
    type.toLowerCase().includes(planTypeSearchTerm.toLowerCase()),
  );

  // Fetch plan types
  useEffect(() => {
    const fetchPlanTypes = async () => {
      setIsPlanTypesLoading(true);
      try {
        const data = await getMealPlanTypeList();
        if (data.success === "1" && Array.isArray(data.meal_plan_data)) {
          setPlanTypes(
            data.meal_plan_data.map(
              (item: { meal_plan_type_name: string }) => item.meal_plan_type_name.trim(),
            ),
          );
        }
      } catch (err) {
        console.error("Failed to fetch meal plan types:", err);
        showError("Failed to load plan types", "Could not retrieve plan type options. Please try again.");
      } finally {
        setIsPlanTypesLoading(false);
      }
    };
    fetchPlanTypes();
  }, []);

  // Fetch adhoc plan categories — only when the adhoc flow is active.
  useEffect(() => {
    if (!isAdhocMealMode) return;
    let cancelled = false;
    (async () => {
      setIsAdhocCategoriesLoading(true);
      try {
        const data = await getAdhocMealPlanCategories();
        if (cancelled) return;
        if (data?.success && Array.isArray(data.data)) {
          const mapped = data.data
            .map((c: any) => ({ id: c.category_id, name: c.category_name }))
            .filter((c: { id: unknown; name: unknown }) => c.id != null && c.name);
          setAdhocCategories(mapped);
        }
      } catch (err) {
        console.error("Failed to fetch adhoc categories:", err);
      } finally {
        if (!cancelled) setIsAdhocCategoriesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdhocMealMode]);

  // Sync plan type search term
  useEffect(() => {
    setPlanTypeSearchTerm(step1Data.planType);
  }, [step1Data.planType]);

  // Click outside plan type dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (planTypeDropdownRef.current && !planTypeDropdownRef.current.contains(event.target as Node)) {
        setShowPlanTypeDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // CPF ratio auto-calculation
  useEffect(() => {
    const minResult = calculateCPFRatioFromStrings(
      step1Data.totalNetCarbs.min,
      step1Data.totalAdjProtein.min,
      step1Data.totalFats.min,
      step1Data.totalCalories.min,
    );
    const maxResult = calculateCPFRatioFromStrings(
      step1Data.totalNetCarbs.max,
      step1Data.totalAdjProtein.max,
      step1Data.totalFats.max,
      step1Data.totalCalories.max,
    );
    setStep1Data((prev) => ({
      ...prev,
      cpfRatio: { min: minResult.ratio, max: maxResult.ratio },
    }));
  }, [
    step1Data.totalNetCarbs.min, step1Data.totalNetCarbs.max,
    step1Data.totalAdjProtein.min, step1Data.totalAdjProtein.max,
    step1Data.totalFats.min, step1Data.totalFats.max,
    step1Data.totalCalories.min, step1Data.totalCalories.max,
  ]);

  const validateMinMaxField = (field: keyof Step1Data, label: string) => {
    const value = step1Data[field] as { min: string; max: string };
    if (value.min && value.max) {
      const min = parseFloat(value.min);
      const max = parseFloat(value.max);
      if (!isNaN(min) && !isNaN(max) && min > max) {
        setValidationErrors((prev) => ({ ...prev, [field]: `${label} min value cannot be greater than max value` }));
      } else {
        setValidationErrors((prev) => { const e = { ...prev }; delete e[field]; return e; });
      }
    }
  };

  useEffect(() => { validateMinMaxField("totalCalories", "Total calories"); }, [step1Data.totalCalories.min, step1Data.totalCalories.max]);
  useEffect(() => { validateMinMaxField("totalAdjProtein", "Total Adj.Protein"); }, [step1Data.totalAdjProtein.min, step1Data.totalAdjProtein.max]);
  useEffect(() => { validateMinMaxField("totalNetCarbs", "Total Net Carbs"); }, [step1Data.totalNetCarbs.min, step1Data.totalNetCarbs.max]);
  useEffect(() => { validateMinMaxField("totalFats", "Total fats"); }, [step1Data.totalFats.min, step1Data.totalFats.max]);
  useEffect(() => { validateMinMaxField("totalGL", "Total GL"); }, [step1Data.totalGL.min, step1Data.totalGL.max]);

  const isStep1Complete = () => {
    const hasNegativeCPF =
      parseFloat(step1Data.cpfRatio.min.c) < 0 || parseFloat(step1Data.cpfRatio.min.p) < 0 ||
      parseFloat(step1Data.cpfRatio.min.f) < 0 || parseFloat(step1Data.cpfRatio.max.c) < 0 ||
      parseFloat(step1Data.cpfRatio.max.p) < 0 || parseFloat(step1Data.cpfRatio.max.f) < 0;

    const baseComplete = (
      step1Data.planType &&
      step1Data.totalCalories.min && step1Data.totalCalories.max &&
      step1Data.totalAdjProtein.min && step1Data.totalAdjProtein.max &&
      step1Data.totalNetCarbs.min && step1Data.totalNetCarbs.max &&
      step1Data.totalFats.min && step1Data.totalFats.max &&
      step1Data.totalGL.min && step1Data.totalGL.max &&
      Object.keys(validationErrors).length === 0 &&
      !hasNegativeCPF
    );

    if (isGlobalTemplateMode) {
      return baseComplete && globalPlanName.trim().length > 0;
    }
    if (isAdhocMealMode) {
      return (
        baseComplete &&
        adhocPlanName.trim().length > 0 &&
        adhocCategoryId != null &&
        isClientContactComplete(adhocContact)
      );
    }
    return baseComplete;
  };

  const handleAddOrUpdateMeal = () => {
    if (!currentMeal.mealType || currentMeal.hour === "Hr" || currentMeal.minute === "Min") return;
    if (selectedMealId) {
      setMealTimings((prev) =>
        prev.map((meal) =>
          meal.id === selectedMealId
            ? { ...meal, mealType: currentMeal.mealType, time: { hour: currentMeal.hour, minute: currentMeal.minute, period: currentMeal.period } }
            : meal,
        ),
      );
      setSelectedMealId(null);
    } else {
      const newMeal: MealTiming = {
        id: Date.now().toString(),
        mealType: currentMeal.mealType,
        time: { hour: currentMeal.hour, minute: currentMeal.minute, period: currentMeal.period },
      };
      setMealTimings((prev) => [...prev, newMeal]);
    }
    setCurrentMeal({ mealType: "", hour: "Hr", minute: "Min", period: "AM" });
  };

  const handleSelectMeal = (meal: MealTiming) => {
    setSelectedMealId(meal.id);
    setCurrentMeal({ mealType: meal.mealType, hour: meal.time.hour, minute: meal.time.minute, period: meal.time.period });
  };

  const handleDeleteMeal = (id: string) => {
    setMealTimings((prev) => prev.filter((meal) => meal.id !== id));
    if (selectedMealId === id) {
      setSelectedMealId(null);
      setCurrentMeal({ mealType: "", hour: "Hr", minute: "Min", period: "AM" });
    }
  };

  const handleReset = () => {
    setCurrentMeal({ mealType: "", hour: "Hr", minute: "Min", period: "AM" });
    setSelectedMealId(null);
  };

  const handleCreate = async () => {
    if (!user?.nutrist_id) {
      showError("Authentication required", "Unable to create: User not authenticated");
      return;
    }
    if (mealTimings.length === 0) {
      warning("No meal timings", "Please add at least one meal timing");
      return;
    }

    setIsCreating(true);
    try {
      const mealDetails = mealTimings.map((meal) => ({
        meal_type_name: meal.mealType,
        hour: parseInt(meal.time.hour, 10),
        minute: parseInt(meal.time.minute, 10),
        period: meal.time.period,
      }));

      const cpfMinValue = `${step1Data.cpfRatio.min.c}:${step1Data.cpfRatio.min.p}:${step1Data.cpfRatio.min.f}`;
      const cpfMaxValue = `${step1Data.cpfRatio.max.c}:${step1Data.cpfRatio.max.p}:${step1Data.cpfRatio.max.f}`;

      // ── Adhoc meal plan creation ─────────────────────────────────────────
      if (isAdhocMealMode) {
        if (adhocCategoryId == null) {
          warning("Category required", "Please select a category");
          setIsCreating(false);
          return;
        }
        const adhocPayload: Record<string, unknown> = {
          plan_name:          adhocPlanName,
          nutritionist_id:    user.nutrist_id,
          admin_id:           null,
          category_id:        adhocCategoryId,
          first_name:         adhocContact.firstName,
          last_name:          adhocContact.lastName,
          location:           adhocContact.location,
          contact_number:     adhocContact.phoneNumber || null,
          client_email:       "",
          CPF_min:            cpfMinValue,
          CPF_max:            cpfMaxValue,
          meal_details:       mealDetails,
        };

        if (step1Data.planType)            adhocPayload.meal_plan_type    = step1Data.planType;
        if (step1Data.instructions)        adhocPayload.instructions       = step1Data.instructions;
        if (step1Data.totalCalories.min)   adhocPayload.total_calories_min = step1Data.totalCalories.min;
        if (step1Data.totalCalories.max)   adhocPayload.total_calories_max = step1Data.totalCalories.max;
        if (step1Data.totalAdjProtein.min) adhocPayload.total_protein_min  = step1Data.totalAdjProtein.min;
        if (step1Data.totalAdjProtein.max) adhocPayload.total_protein_max  = step1Data.totalAdjProtein.max;
        if (step1Data.totalNetCarbs.min)   adhocPayload.total_carbs_min    = step1Data.totalNetCarbs.min;
        if (step1Data.totalNetCarbs.max)   adhocPayload.total_carbs_max    = step1Data.totalNetCarbs.max;
        if (step1Data.totalFats.min)       adhocPayload.total_fats_min     = step1Data.totalFats.min;
        if (step1Data.totalFats.max)       adhocPayload.total_fats_max     = step1Data.totalFats.max;
        if (step1Data.totalGL.min)         adhocPayload.total_GL_min       = step1Data.totalGL.min;
        if (step1Data.totalGL.max)         adhocPayload.total_GL_max       = step1Data.totalGL.max;

        const apiResponse = await createAdhocMealPlanFromScratch(adhocPayload);

        if (apiResponse.success === true) {
          const adhocPlanId = apiResponse.data?.adhoc_plan_id;
          if (!adhocPlanId) throw new Error("Adhoc plan created but ID not returned from server");

          navigate(`/adhoc-meal/${adhocPlanId}`, {
            state: {
              showSuccessToast: true,
              adhocMealMode: true,
            },
            replace: false,
          });
        } else {
          throw new Error(apiResponse.message || "Failed to create adhoc plan");
        }
        return;
      }

      // ── Global template creation ──────────────────────────────────────────
      if (isGlobalTemplateMode) {
        const requestBody: Record<string, unknown> = {
          template_name:      globalPlanName,
          nutritionist_id:    user.nutrist_id,
          admin_id:           null,
          visibility:         "global",
          CPF_min:            cpfMinValue,
          CPF_max:            cpfMaxValue,
          meal_details:       mealDetails,
        };

        if (step1Data.planType)           requestBody.meal_plan_type      = step1Data.planType;
        if (globalGoal)                   requestBody.plan_goal            = globalGoal;
        if (step1Data.instructions)       requestBody.instructions         = step1Data.instructions;
        if (step1Data.totalCalories.min)  requestBody.total_calories_min   = step1Data.totalCalories.min;
        if (step1Data.totalCalories.max)  requestBody.total_calories_max   = step1Data.totalCalories.max;
        if (step1Data.totalAdjProtein.min) requestBody.total_protein_min   = step1Data.totalAdjProtein.min;
        if (step1Data.totalAdjProtein.max) requestBody.total_protein_max   = step1Data.totalAdjProtein.max;
        if (step1Data.totalNetCarbs.min)  requestBody.total_carbs_min      = step1Data.totalNetCarbs.min;
        if (step1Data.totalNetCarbs.max)  requestBody.total_carbs_max      = step1Data.totalNetCarbs.max;
        if (step1Data.totalFats.min)      requestBody.total_fats_min       = step1Data.totalFats.min;
        if (step1Data.totalFats.max)      requestBody.total_fats_max       = step1Data.totalFats.max;
        if (step1Data.totalGL.min)        requestBody.total_GL_min         = step1Data.totalGL.min;
        if (step1Data.totalGL.max)        requestBody.total_GL_max         = step1Data.totalGL.max;

        const apiResponse = await createGlobalTemplate(requestBody);

        if (apiResponse.success === true) {
          const templateId = apiResponse.data?.template_id;
          if (!templateId) throw new Error("Template created but ID not returned from server");

          const newMealPlan = {
            id:         templateId,
            plan_name:  apiResponse.data.template_name || globalPlanName,
            meal_type:  step1Data.planType,
            created_by: `${user.nutrist_fname || ""} ${user.nutrist_lname || ""}`.trim(),
            created_on: new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
            cal:        parseFloat(step1Data.totalCalories.max) || 0,
            adj_prot:   parseFloat(step1Data.totalAdjProtein.max) || 0,
            carb:       parseFloat(step1Data.totalNetCarbs.max) || 0,
            fat:        parseFloat(step1Data.totalFats.max) || 0,
            gl:         parseFloat(step1Data.totalGL.max) || 0,
            status:     "Draft" as const,
          };

          navigate(`/client/${effectiveUserId}/plans/meal/${templateId}`, {
            state: {
              mealPlan:          newMealPlan,
              mealTimings:       apiResponse.data.meal_details ?? mealTimings,
              isNewPlan:         true,
              showSuccessToast:  true,
              fromGlobal:        true,
              globalTemplateMode: true,
            },
            replace: false,
          });
        } else {
          throw new Error(apiResponse.message || "Failed to create template");
        }
        return;
      }

      // ── Client meal plan creation ─────────────────────────────────────────
      const requestBody = {
        nutritionist_id:    user.nutrist_id,
        user_id:            userIdNumber,
        meal_plan_name:     planName,
        meal_plan_type:     step1Data.planType,
        total_calories_min: step1Data.totalCalories.min,
        total_calories_max: step1Data.totalCalories.max,
        total_protein_min:  step1Data.totalAdjProtein.min,
        total_protein_max:  step1Data.totalAdjProtein.max,
        total_carbs_min:    step1Data.totalNetCarbs.min,
        total_carbs_max:    step1Data.totalNetCarbs.max,
        total_fats_min:     step1Data.totalFats.min,
        total_fats_max:     step1Data.totalFats.max,
        total_GL_min:       step1Data.totalGL.min,
        total_GL_max:       step1Data.totalGL.max,
        CPF_min:            cpfMinValue,
        CPF_max:            cpfMaxValue,
        instructions:       step1Data.instructions || "",
        meal_status:        "0",
        meal_details:       mealDetails,
      };

      const apiResponse = await addMealPlanDetails(requestBody);

      if (apiResponse.success === "1") {
        const mealPlanId =
          apiResponse.meal_plan_data?.meal_plan_id ||
          apiResponse.meal_plan_id ||
          apiResponse.data?.meal_plan_id;

        if (!mealPlanId) throw new Error("Meal plan created but ID not returned from server");

        const newMealPlan = {
          id:         mealPlanId,
          plan_name:  apiResponse.meal_plan_data?.meal_plan_name || planName,
          meal_type:  apiResponse.meal_plan_data?.meal_plan_type || step1Data.planType,
          created_by: user
            ? `${user.nutrist_fname || ""} ${user.nutrist_lname || ""}`.trim()
            : "Unknown",
          created_on: new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
          cal:        parseFloat(step1Data.totalCalories.max) || 0,
          adj_prot:   parseFloat(step1Data.totalAdjProtein.max) || 0,
          carb:       parseFloat(step1Data.totalNetCarbs.max) || 0,
          fat:        parseFloat(step1Data.totalFats.max) || 0,
          gl:         parseFloat(step1Data.totalGL.max) || 0,
          status:     "Draft" as const,
        };

        navigate(`/client/${effectiveUserId}/plans/meal/${mealPlanId}`, {
          state: {
            mealPlan:         newMealPlan,
            mealTimings,
            isNewPlan:        true,
            showSuccessToast: true,
          },
          replace: false,
        });
      } else {
        throw new Error(apiResponse.message || "Failed to create meal plan");
      }
    } catch (err) {
      console.error("Error creating:", err);
      showError("Failed to create", err instanceof Error ? err.message : "Please try again");
    } finally {
      setIsCreating(false);
    }
  };

  const formatTime = (hour: string, minute: string, period: string) => `${hour}:${minute} ${period}`;

  const shouldShowError = (field: string) => touchedFields[field] && validationErrors[field] !== undefined;
  const isFieldEmpty = (value: string) => !value || value.trim() === "";
  const shouldShowEmptyError = (field: string, value: string) => touchedFields[field] && isFieldEmpty(value);
  const markFieldTouched = (field: string) => setTouchedFields((prev) => ({ ...prev, [field]: true }));

  const getTimeInMinutes = (meal: MealTiming) => {
    let hour = parseInt(meal.time.hour, 10);
    const minute = parseInt(meal.time.minute, 10);
    if (meal.time.period === "PM" && hour !== 12) hour += 12;
    if (meal.time.period === "AM" && hour === 12) hour = 0;
    return hour * 60 + minute;
  };

  const sortedMealTimings = [...mealTimings].sort((a, b) => getTimeInMinutes(a) - getTimeInMinutes(b));

  const handleCancel = () =>
    isGlobalTemplateMode
      ? navigate("/global-template")
      : isAdhocMealMode
        ? navigate("/adhoc-meal")
        : navigate(`/client/${userId}/plans`);

  // ── Step 2 counts ─────────────────────────────────────────────────────────
  const [planCounts, setPlanCounts] = useState<{
    health_goal: string;
    template_count: number;
    client_count: number;
    plan_count: number;
  } | null>(null);

  useEffect(() => {
    if (currentStep !== 2 || !userIdNumber) return;
    getMealPlanCount(
      userIdNumber,
      step1Data.totalCalories.min,
      step1Data.totalCalories.max,
      step1Data.totalAdjProtein.min,
      step1Data.totalAdjProtein.max,
    )
      .then((data) => { if (data.success && data.data) setPlanCounts(data.data); })
      .catch(() => {});
  }, [currentStep, userIdNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Step 2 card data ──────────────────────────────────────────────────────────
  const templateFooter = planCounts
    ? `${planCounts.template_count} match ${planCounts.health_goal}`
    : "Loading…";
  const clientFooter = planCounts
    ? `${planCounts.client_count} client${planCounts.client_count !== 1 ? "s" : ""} · ${planCounts.plan_count} plan${planCounts.plan_count !== 1 ? "s" : ""}`
    : "Loading…";

  const step2Cards = [
    {
      id: "template" as const,
      icon: <UseTempletIcon className="w-8 h-8" />,
      activeIcon: <UseTempletActiveIcon className="w-8 h-8" />,
      title: "Use a template",
      description: "Pick from standardized plans matching this client's goal.",
      badge: "Recommended",
      footer: templateFooter,
    },
    {
      id: "client" as const,
      icon: <CopyClientIcon className="w-8 h-8" />,
      activeIcon: <CopyClientActiveIcon className="w-8 h-8" />,
      title: "Copy from client",
      description: "Reuse a plan you built for another client.",
      badge: null,
      footer: clientFooter,
    },
    {
      id: "scratch" as const,
      icon: <FromScratchIcon className="w-8 h-8" />,
      activeIcon: <FromScratchActiveIcon className="w-8 h-8" />,
      title: "Start from scratch",
      description: "Build meal by meal. Full control, no prefilled data.",
      badge: null,
      footer: "Takes ~15 min",
    },
  ];

  // ── Pagination constants ─────────────────────────────────────────────────
  const TEMPLATE_PAGE_SIZE = 12;
  const CLIENT_PAGE_SIZE = 12;

  // ── Template browser state ────────────────────────────────────────────────
  const [templateSearch, setTemplateSearch] = useState("");
  const [previewTemplate, setPreviewTemplate] = useState<MealTemplate | null>(null);
  const [templatePlanTypeFilter, setTemplatePlanTypeFilter] = useState("");
  const [templateCategoryFilter, setTemplateCategoryFilter] = useState("");
  const [templateOptionsFilter, setTemplateOptionsFilter] = useState("");
  const [templateMealTypeFilter, setTemplateMealTypeFilter] = useState("");
  const [templateGoalFilter, setTemplateGoalFilter] = useState("");
  const [templateFilterMode, setTemplateFilterMode] = useState<"recommendation" | "filter">("recommendation");
  const [activeTemplateTags, setActiveTemplateTags] = useState<{ cal: boolean; protein: boolean; carb: boolean; fat: boolean; gl: boolean }>({ cal: false, protein: false, carb: false, fat: false, gl: false });
  const [apiTemplates, setApiTemplates] = useState<MealTemplate[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isLoadingClientPreview, setIsLoadingClientPreview] = useState(false);
  const [templatePage, setTemplatePage] = useState(1);
  const [templateTotal, setTemplateTotal] = useState(0);
  const templateDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset template pagination whenever filters/mode change
  useEffect(() => {
    setTemplatePage(1);
  }, [
    templateSearch,
    templatePlanTypeFilter,
    templateOptionsFilter,
    templateMealTypeFilter,
    templateGoalFilter,
    templateCategoryFilter,
    templateFilterMode,
    activeTemplateTags,
  ]);

  // Switch template mode without flashing the previous mode's badges: clear
  // the current results and show the spinner immediately so the debounced
  // fetch takes over cleanly.
  const handleTemplateModeChange = (mode: "recommendation" | "filter") => {
    if (mode === templateFilterMode) return;
    setApiTemplates([]);
    setTemplateTotal(0);
    setTemplatePage(1);
    setIsLoadingTemplates(true);
    if (mode === "filter") {
      setActiveTemplateTags({ cal: false, protein: false, carb: false, fat: false, gl: false });
    }
    setTemplateFilterMode(mode);
  };

  const handleClientModeChange = (mode: "recommendation" | "filter") => {
    if (mode === clientFilterMode) return;
    setApiClientPlans([]);
    setClientTotal(0);
    setClientPage(1);
    setIsClientPlansLoading(true);
    if (mode === "filter") {
      setActiveClientTags({ cal: false, protein: false, carb: false, fat: false, gl: false });
    }
    setClientFilterMode(mode);
  };

  const handlePreviewTemplate = async (template: MealTemplate) => {
    setIsLoadingPreview(true);
    setPreviewTemplate(template);
    try {
      const data = await getGlobalTemplateDetails(template.id);
      if (data.success && data.data) {
        setPreviewTemplate(mapApiTemplateDetail(data.data as ApiTemplateDetail, template));
      }
    } catch {
      // keep the base template data already set
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handlePreviewClientPlan = async (plan: ClientPlan) => {
    setIsLoadingClientPreview(true);
    setPreviewClientPlan(plan);
    try {
      const data = await getAssignedClientMealPlanPreview(plan.id);
      if (data.success && data.data) {
        setPreviewClientPlan(mapApiClientPlanDetail(data.data as ApiClientPlanDetail, plan));
      }
    } catch {
      // keep the base plan data already set
    } finally {
      setIsLoadingClientPreview(false);
    }
  };

  useEffect(() => {
    if (step2Selection !== "template") return;
    // Show spinner immediately so filter changes don't briefly re-render the stale grid
    setIsLoadingTemplates(true);
    if (templateDebounceRef.current) clearTimeout(templateDebounceRef.current);
    templateDebounceRef.current = setTimeout(async () => {
      try {
        let data: any;
        const paging = { page: templatePage, page_size: TEMPLATE_PAGE_SIZE };
        const effectiveTemplateGoal = templateGoalFilter || planCounts?.health_goal || "";
        if (templateFilterMode === "filter") {
          const body: Record<string, unknown> = { ...paging };
          if (effectiveTemplateGoal) body.target_goal = effectiveTemplateGoal;
          if (activeTemplateTags.cal && step1Data.totalCalories.min) body.cal_min = parseInt(step1Data.totalCalories.min, 10);
          if (activeTemplateTags.cal && step1Data.totalCalories.max) body.cal_max = parseInt(step1Data.totalCalories.max, 10);
          if (activeTemplateTags.protein && step1Data.totalAdjProtein.min) body.protein_min = parseInt(step1Data.totalAdjProtein.min, 10);
          if (activeTemplateTags.protein && step1Data.totalAdjProtein.max) body.protein_max = parseInt(step1Data.totalAdjProtein.max, 10);
          if (activeTemplateTags.carb && step1Data.totalNetCarbs.min) body.carbs_min = parseInt(step1Data.totalNetCarbs.min, 10);
          if (activeTemplateTags.carb && step1Data.totalNetCarbs.max) body.carbs_max = parseInt(step1Data.totalNetCarbs.max, 10);
          if (activeTemplateTags.fat && step1Data.totalFats.min) body.fat_min = parseInt(step1Data.totalFats.min, 10);
          if (activeTemplateTags.fat && step1Data.totalFats.max) body.fat_max = parseInt(step1Data.totalFats.max, 10);
          if (activeTemplateTags.gl && step1Data.totalGL.min) body.gl_min = parseInt(step1Data.totalGL.min, 10);
          if (activeTemplateTags.gl && step1Data.totalGL.max) body.gl_max = parseInt(step1Data.totalGL.max, 10);
          if (templateSearch.trim()) body.search = templateSearch.trim();
          if (templatePlanTypeFilter) body.meal_plan_type = templatePlanTypeFilter;
          if (templateMealTypeFilter) body.meals_per_day = parseInt(templateMealTypeFilter, 10);
          if (templateOptionsFilter) {
            const m = templateOptionsFilter.match(/(\d+)/);
            if (m) body.options_per_meal = parseInt(m[1], 10);
          }
          data = await getMealPlanFilterTemplate(userIdNumber, body);
        } else {
          const body: Record<string, unknown> = { ...paging };
          if (effectiveTemplateGoal) body.target_goal = effectiveTemplateGoal;
          if (step1Data.totalCalories.min) body.cal_min = parseInt(step1Data.totalCalories.min, 10);
          if (step1Data.totalCalories.max) body.cal_max = parseInt(step1Data.totalCalories.max, 10);
          if (step1Data.totalAdjProtein.min) body.protein_min = parseInt(step1Data.totalAdjProtein.min, 10);
          if (step1Data.totalAdjProtein.max) body.protein_max = parseInt(step1Data.totalAdjProtein.max, 10);
          if (templateSearch.trim()) body.search = templateSearch.trim();
          if (templatePlanTypeFilter) body.meal_plan_type = templatePlanTypeFilter;
          if (templateMealTypeFilter) body.meals_per_day = parseInt(templateMealTypeFilter, 10);
          if (templateOptionsFilter) {
            const m = templateOptionsFilter.match(/(\d+)/);
            if (m) body.options_per_meal = parseInt(m[1], 10);
          }
          data = await getGlobalTemplatesSuggest(userIdNumber, body);
        }
        if (data.success) {
          const rawList: ApiTemplateItem[] = Array.isArray(data.data)
            ? data.data
            : Array.isArray(data.data?.results)
              ? data.data.results
              : [];
          const items = rawList.map(mapApiTemplate);
          setApiTemplates(items);
          const total: number | undefined = data.data?.total;
          setTemplateTotal(typeof total === "number" ? total : items.length);
        } else {
          setApiTemplates([]);
          setTemplateTotal(0);
        }
      } catch {
        setApiTemplates([]);
        setTemplateTotal(0);
      } finally {
        setIsLoadingTemplates(false);
      }
    }, 400);
    return () => { if (templateDebounceRef.current) clearTimeout(templateDebounceRef.current); };
  }, [step2Selection, step1Data, templateSearch, templatePlanTypeFilter, templateMealTypeFilter, templateOptionsFilter, templateGoalFilter, templateFilterMode, activeTemplateTags, templatePage, planCounts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Category (Veg/Non-Veg) is filtered client-side. Goal is now sent as
  // `health_goal` to the server, but we keep the client-side check as a
  // defensive fallback when the backend doesn't scope by goal.
  const filteredTemplates = apiTemplates.filter((t) => {
    const matchesCategory = !templateCategoryFilter || (
      templateCategoryFilter === "Veg"
        ? t.planType.toLowerCase().includes("veg") && !t.planType.toLowerCase().includes("non")
        : t.planType.toLowerCase().includes("non")
    );
    const matchesGoal = !templateGoalFilter || t.goal.toLowerCase() === templateGoalFilter.toLowerCase();
    return matchesCategory && matchesGoal;
  });

  // ── Copy-from-client browser state ───────────────────────────────────────
  const [clientSearch, setClientSearch] = useState("");
  const [clientPlanTypeFilter, setClientPlanTypeFilter] = useState("");
  const [clientCategoryFilter] = useState("");
  const [clientOptionsFilter, setClientOptionsFilter] = useState("");
  const [clientMealsFilter, setClientMealsFilter] = useState("");
  const [clientNameFilter, setClientNameFilter] = useState("");
  const [clientGoalFilter, setClientGoalFilter] = useState("");
  const [clientNutritionistFilter, setClientNutritionistFilter] = useState<string[]>([]);
  const [clientFilterMode, setClientFilterMode] = useState<"recommendation" | "filter">("recommendation");
  const [activeClientTags, setActiveClientTags] = useState<{ cal: boolean; protein: boolean; carb: boolean; fat: boolean; gl: boolean }>({ cal: false, protein: false, carb: false, fat: false, gl: false });
  const [nutritionistsList, setNutritionistsList] = useState<{ id: string; name: string }[]>([]);
  const [isNutritionistsLoading, setIsNutritionistsLoading] = useState(false);
  const [clientList, setClientList] = useState<{ id: number; name: string; phone: string }[]>([]);
  const [apiClientPlans, setApiClientPlans] = useState<ClientPlan[]>([]);
  const [isClientPlansLoading, setIsClientPlansLoading] = useState(false);
  const [clientPage, setClientPage] = useState(1);
  const [clientTotal, setClientTotal] = useState(0);
  const clientDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [previewClientPlan, setPreviewClientPlan] = useState<ClientPlan | null>(null);

  // Reset client pagination whenever filters/mode change
  useEffect(() => {
    setClientPage(1);
  }, [
    clientSearch,
    clientPlanTypeFilter,
    clientMealsFilter,
    clientOptionsFilter,
    clientNutritionistFilter,
    clientNameFilter,
    clientGoalFilter,
    clientFilterMode,
    activeClientTags,
  ]);

  // Fetch nutritionists once when entering the client tab; pre-select all
  useEffect(() => {
    if (step2Selection !== "client") return;
    setIsNutritionistsLoading(true);
    getAvailableNutritionistsList()
      .then((data) => {
        if (data.success && Array.isArray(data.data)) {
          const list = (data.data as Array<{ nutrist_id: number; nutrist_fname?: string; nutrist_lname?: string }>).map((n) => ({
            id: String(n.nutrist_id),
            name: `${n.nutrist_fname ?? ""} ${n.nutrist_lname ?? ""}`.trim(),
          }));
          setNutritionistsList(list);
          setClientNutritionistFilter(list.map((n) => n.name));
        }
      })
      .catch(() => {})
      .finally(() => setIsNutritionistsLoading(false));
  }, [step2Selection]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch client list once when entering the client tab (feeds the "Select client" dropdown)
  useEffect(() => {
    if (step2Selection !== "client") return;
    getClientListForMealPlan()
      .then((data) => {
        if (data.success && Array.isArray(data.data)) {
          setClientList(
            (data.data as Array<{ user_id: number; name?: string; phone?: string }>).map((c) => ({
              id: c.user_id,
              name: c.name ?? "",
              phone: c.phone ?? "",
            })),
          );
        }
      })
      .catch(() => {});
  }, [step2Selection]);

  // Debounced fetch of client plans from MEAL_PLAN_SUGGEST
  useEffect(() => {
    if (step2Selection !== "client") return;
    // Show spinner immediately so filter changes don't briefly re-render the stale grid
    setIsClientPlansLoading(true);
    if (clientDebounceRef.current) clearTimeout(clientDebounceRef.current);
    clientDebounceRef.current = setTimeout(async () => {
      try {
        let data: any;
        const paging = { page: clientPage, page_size: CLIENT_PAGE_SIZE };
        const selectedClient = clientNameFilter
          ? clientList.find((c) => c.name === clientNameFilter)
          : undefined;
        const effectiveClientGoal = clientGoalFilter || planCounts?.health_goal || "";
        if (clientFilterMode === "filter") {
          const body: Record<string, unknown> = { ...paging };
          if (effectiveClientGoal) body.target_goal = effectiveClientGoal;
          if (selectedClient) body.filter_user_id = selectedClient.id;
          if (activeClientTags.cal && step1Data.totalCalories.min) body.cal_min = parseInt(step1Data.totalCalories.min, 10);
          if (activeClientTags.cal && step1Data.totalCalories.max) body.cal_max = parseInt(step1Data.totalCalories.max, 10);
          if (activeClientTags.protein && step1Data.totalAdjProtein.min) body.protein_min = parseInt(step1Data.totalAdjProtein.min, 10);
          if (activeClientTags.protein && step1Data.totalAdjProtein.max) body.protein_max = parseInt(step1Data.totalAdjProtein.max, 10);
          if (activeClientTags.carb && step1Data.totalNetCarbs.min) body.carbs_min = parseInt(step1Data.totalNetCarbs.min, 10);
          if (activeClientTags.carb && step1Data.totalNetCarbs.max) body.carbs_max = parseInt(step1Data.totalNetCarbs.max, 10);
          if (activeClientTags.fat && step1Data.totalFats.min) body.fat_min = parseInt(step1Data.totalFats.min, 10);
          if (activeClientTags.fat && step1Data.totalFats.max) body.fat_max = parseInt(step1Data.totalFats.max, 10);
          if (activeClientTags.gl && step1Data.totalGL.min) body.gl_min = parseInt(step1Data.totalGL.min, 10);
          if (activeClientTags.gl && step1Data.totalGL.max) body.gl_max = parseInt(step1Data.totalGL.max, 10);
          if (clientSearch.trim()) body.search = clientSearch.trim();
          if (clientPlanTypeFilter) body.meal_plan_type = clientPlanTypeFilter;
          if (clientMealsFilter) body.meals_per_day = parseInt(clientMealsFilter, 10);
          if (clientOptionsFilter) {
            const m = clientOptionsFilter.match(/(\d+)/);
            if (m) body.options_per_meal = parseInt(m[1], 10);
          }
          // Send nutritionist_ids for the current selection — all when all are selected,
          // a subset when only some are picked.
          if (clientNutritionistFilter.length > 0 && nutritionistsList.length > 0) {
            body.nutritionist_ids = nutritionistsList
              .filter((n) => clientNutritionistFilter.includes(n.name))
              .map((n) => parseInt(n.id, 10));
          }
          if (userIdNumber) body.exclude_user_id = userIdNumber;
          data = await getMealPlanFilterAssignedClient(userIdNumber, body);
        } else {
          const body: Record<string, unknown> = { ...paging };
          if (effectiveClientGoal) body.target_goal = effectiveClientGoal;
          if (selectedClient) body.filter_user_id = selectedClient.id;
          if (step1Data.totalCalories.min) body.cal_min = parseInt(step1Data.totalCalories.min, 10);
          if (step1Data.totalCalories.max) body.cal_max = parseInt(step1Data.totalCalories.max, 10);
          if (step1Data.totalAdjProtein.min) body.protein_min = parseInt(step1Data.totalAdjProtein.min, 10);
          if (step1Data.totalAdjProtein.max) body.protein_max = parseInt(step1Data.totalAdjProtein.max, 10);
          if (clientSearch.trim()) body.search = clientSearch.trim();
          if (clientPlanTypeFilter) body.meal_plan_type = clientPlanTypeFilter;
          if (clientMealsFilter) body.meals_per_day = parseInt(clientMealsFilter, 10);
          if (clientOptionsFilter) {
            const m = clientOptionsFilter.match(/(\d+)/);
            if (m) body.options_per_meal = parseInt(m[1], 10);
          }
          // Send nutritionist_ids for the current selection — all when all are selected,
          // a subset when only some are picked.
          if (clientNutritionistFilter.length > 0 && nutritionistsList.length > 0) {
            body.nutritionist_ids = nutritionistsList
              .filter((n) => clientNutritionistFilter.includes(n.name))
              .map((n) => parseInt(n.id, 10));
          }
          if (userIdNumber) body.exclude_user_id = userIdNumber;
          data = await getMealPlanSuggest(userIdNumber, body);
        }
        if (data.success) {
          const rawList: ApiClientPlanItem[] = Array.isArray(data.data)
            ? data.data
            : Array.isArray(data.data?.results)
              ? data.data.results
              : [];
          const items = rawList.map(mapApiClientPlan);
          setApiClientPlans(items);
          const total: number | undefined = data.data?.total;
          setClientTotal(typeof total === "number" ? total : items.length);
        } else {
          setApiClientPlans([]);
          setClientTotal(0);
        }
      } catch {
        setApiClientPlans([]);
        setClientTotal(0);
      } finally {
        setIsClientPlansLoading(false);
      }
    }, 400);
    return () => { if (clientDebounceRef.current) clearTimeout(clientDebounceRef.current); };
  }, [step2Selection, step1Data, clientSearch, clientPlanTypeFilter, clientMealsFilter, clientOptionsFilter, clientNutritionistFilter, nutritionistsList, userIdNumber, clientFilterMode, activeClientTags, clientPage, clientGoalFilter, clientNameFilter, clientList, planCounts]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredClientPlans = apiClientPlans.filter((p) => {
    const matchesClient = !clientNameFilter || p.clientName === clientNameFilter;
    const matchesGoal = !clientGoalFilter || p.goal.toLowerCase() === clientGoalFilter.toLowerCase();
    return matchesClient && matchesGoal;
  });

  const clientNameOptions = clientList.map((c) => c.name).filter((n) => !!n);

  // Builds the payload shared by both "from template" and "from client plan"
  // adhoc create flows — client contact + category + step-1 nutrient/CPF
  // fields. `overrides` is intentionally omitted; no UI collects it today.
  const buildAdhocFromSourcePayload = (): Record<string, unknown> => {
    const cpfMin = `${step1Data.cpfRatio.min.c}:${step1Data.cpfRatio.min.p}:${step1Data.cpfRatio.min.f}`;
    const cpfMax = `${step1Data.cpfRatio.max.c}:${step1Data.cpfRatio.max.p}:${step1Data.cpfRatio.max.f}`;
    const payload: Record<string, unknown> = {
      plan_name:      adhocPlanName,
      category_id:    adhocCategoryId,
      first_name:     adhocContact.firstName,
      last_name:      adhocContact.lastName,
      contact_number: adhocContact.phoneNumber || null,
      client_email:   "",
      location:       adhocContact.location,
    };
    if (step1Data.planType)             payload.meal_plan_type     = step1Data.planType;
    if (step1Data.totalCalories.min)    payload.total_calories_min = parseFloat(step1Data.totalCalories.min);
    if (step1Data.totalCalories.max)    payload.total_calories_max = parseFloat(step1Data.totalCalories.max);
    if (step1Data.totalAdjProtein.min)  payload.total_protein_min  = parseFloat(step1Data.totalAdjProtein.min);
    if (step1Data.totalAdjProtein.max)  payload.total_protein_max  = parseFloat(step1Data.totalAdjProtein.max);
    if (step1Data.totalNetCarbs.min)    payload.total_carbs_min    = parseFloat(step1Data.totalNetCarbs.min);
    if (step1Data.totalNetCarbs.max)    payload.total_carbs_max    = parseFloat(step1Data.totalNetCarbs.max);
    if (step1Data.totalFats.min)        payload.total_fats_min     = parseFloat(step1Data.totalFats.min);
    if (step1Data.totalFats.max)        payload.total_fats_max     = parseFloat(step1Data.totalFats.max);
    if (step1Data.totalGL.min)          payload.total_GL_min       = parseFloat(step1Data.totalGL.min);
    if (step1Data.totalGL.max)          payload.total_GL_max       = parseFloat(step1Data.totalGL.max);
    if (cpfMin !== "::")                payload.CPF_min            = cpfMin;
    if (cpfMax !== "::")                payload.CPF_max            = cpfMax;
    if (step1Data.instructions)         payload.instructions       = step1Data.instructions;
    return payload;
  };

  // The backend returns success as string "1" (unlike the scratch flow which
  // returns boolean true). Accept both shapes.
  const isAdhocCreateSuccess = (r: any): boolean =>
    r?.success === true || r?.success === "1" || r?.success === 1;

  const handleDuplicatePlan = async (plan: MealTemplate) => {
    if (isAdhocMealMode) {
      if (adhocCategoryId == null) {
        warning("Category required", "Please select a category");
        return;
      }
      setIsCreating(true);
      try {
        const payload = {
          ...buildAdhocFromSourcePayload(),
          source_plan_id: parseInt(plan.id, 10),
        };
        const apiResponse = await createAdhocMealPlanFromExistingClientPlan(payload);
        if (isAdhocCreateSuccess(apiResponse)) {
          const adhocPlanId = apiResponse.data?.adhoc_plan_id;
          if (!adhocPlanId) throw new Error("Adhoc plan created but ID not returned from server");
          navigate(`/adhoc-meal/${adhocPlanId}`, {
            state: { showSuccessToast: true, adhocMealMode: true },
            replace: false,
          });
        } else {
          throw new Error(apiResponse.message || "Failed to create adhoc plan from client plan");
        }
      } catch (err) {
        console.error("Error creating adhoc plan from client plan:", err);
        showError(
          "Failed to create adhoc plan",
          err instanceof Error ? err.message : "Please try again",
        );
      } finally {
        setIsCreating(false);
      }
      return;
    }
    if (!user?.nutrist_id) {
      showError("Authentication required", "Unable to duplicate meal plan: User not authenticated");
      return;
    }
    setIsCreating(true);
    try {
      const cpfMin = `${step1Data.cpfRatio.min.c}:${step1Data.cpfRatio.min.p}:${step1Data.cpfRatio.min.f}`;
      const cpfMax = `${step1Data.cpfRatio.max.c}:${step1Data.cpfRatio.max.p}:${step1Data.cpfRatio.max.f}`;
      const body: Record<string, unknown> = {
        source_plan_id: parseInt(plan.id, 10),
        target_user_id: userIdNumber,
        nutritionist_id: user.nutrist_id,
        min_calories: step1Data.totalCalories.min ? parseFloat(step1Data.totalCalories.min) : undefined,
        max_calories: step1Data.totalCalories.max ? parseFloat(step1Data.totalCalories.max) : undefined,
        min_protein: step1Data.totalAdjProtein.min ? parseFloat(step1Data.totalAdjProtein.min) : undefined,
        max_protein: step1Data.totalAdjProtein.max ? parseFloat(step1Data.totalAdjProtein.max) : undefined,
        min_carbs: step1Data.totalNetCarbs.min ? parseFloat(step1Data.totalNetCarbs.min) : undefined,
        max_carbs: step1Data.totalNetCarbs.max ? parseFloat(step1Data.totalNetCarbs.max) : undefined,
        min_fats: step1Data.totalFats.min ? parseFloat(step1Data.totalFats.min) : undefined,
        max_fats: step1Data.totalFats.max ? parseFloat(step1Data.totalFats.max) : undefined,
        min_gl: step1Data.totalGL.min ? parseFloat(step1Data.totalGL.min) : undefined,
        max_gl: step1Data.totalGL.max ? parseFloat(step1Data.totalGL.max) : undefined,
        min_cpf_ratio: cpfMin !== "::" ? cpfMin : undefined,
        max_cpf_ratio: cpfMax !== "::" ? cpfMax : undefined,
        meal_plan_type: step1Data.planType || undefined,
      };
      if (planName) body.meal_plan_name = planName;
      if (step1Data.instructions) body.instructions = step1Data.instructions;

      const apiResponse = await duplicateMealPlan(body);

      if (apiResponse.success) {
        const mealPlanId = apiResponse.data?.meal_plan_id;
        if (!mealPlanId) throw new Error("Meal plan duplicated but ID not returned from server");

        navigate(`/client/${userId}/plans/meal/${mealPlanId}`, {
          state: { showSuccessToast: true },
          replace: false,
        });
      } else {
        throw new Error(apiResponse.message || "Failed to duplicate meal plan");
      }
    } catch (err) {
      console.error("Error duplicating meal plan:", err);
      showError(
        "Failed to duplicate meal plan",
        err instanceof Error ? err.message : "Please try again",
      );
    } finally {
      setIsCreating(false);
    }
  };

  const handleDuplicateTemplate = async (template: MealTemplate) => {
    if (isAdhocMealMode) {
      if (adhocCategoryId == null) {
        warning("Category required", "Please select a category");
        return;
      }
      setIsCreating(true);
      try {
        const payload = {
          ...buildAdhocFromSourcePayload(),
          template_id: parseInt(template.id, 10),
        };
        const apiResponse = await createAdhocMealPlanFromTemplate(payload);
        if (isAdhocCreateSuccess(apiResponse)) {
          const adhocPlanId = apiResponse.data?.adhoc_plan_id;
          if (!adhocPlanId) throw new Error("Adhoc plan created but ID not returned from server");
          navigate(`/adhoc-meal/${adhocPlanId}`, {
            state: { showSuccessToast: true, adhocMealMode: true },
            replace: false,
          });
        } else {
          throw new Error(apiResponse.message || "Failed to create adhoc plan from template");
        }
      } catch (err) {
        console.error("Error creating adhoc plan from template:", err);
        showError(
          "Failed to create adhoc plan",
          err instanceof Error ? err.message : "Please try again",
        );
      } finally {
        setIsCreating(false);
      }
      return;
    }
    if (!user?.nutrist_id) {
      showError("Authentication required", "Unable to create meal plan from template: User not authenticated");
      return;
    }
    setIsCreating(true);
    try {
      const cpfMin = `${step1Data.cpfRatio.min.c}:${step1Data.cpfRatio.min.p}:${step1Data.cpfRatio.min.f}`;
      const cpfMax = `${step1Data.cpfRatio.max.c}:${step1Data.cpfRatio.max.p}:${step1Data.cpfRatio.max.f}`;
      const body: Record<string, unknown> = {
        template_id: parseInt(template.id, 10),
        target_user_id: userIdNumber,
        nutritionist_id: user.nutrist_id,
        min_calories: step1Data.totalCalories.min ? parseFloat(step1Data.totalCalories.min) : undefined,
        max_calories: step1Data.totalCalories.max ? parseFloat(step1Data.totalCalories.max) : undefined,
        min_protein: step1Data.totalAdjProtein.min ? parseFloat(step1Data.totalAdjProtein.min) : undefined,
        max_protein: step1Data.totalAdjProtein.max ? parseFloat(step1Data.totalAdjProtein.max) : undefined,
        min_carbs: step1Data.totalNetCarbs.min ? parseFloat(step1Data.totalNetCarbs.min) : undefined,
        max_carbs: step1Data.totalNetCarbs.max ? parseFloat(step1Data.totalNetCarbs.max) : undefined,
        min_fats: step1Data.totalFats.min ? parseFloat(step1Data.totalFats.min) : undefined,
        max_fats: step1Data.totalFats.max ? parseFloat(step1Data.totalFats.max) : undefined,
        min_gl: step1Data.totalGL.min ? parseFloat(step1Data.totalGL.min) : undefined,
        max_gl: step1Data.totalGL.max ? parseFloat(step1Data.totalGL.max) : undefined,
        min_cpf_ratio: cpfMin !== "::" ? cpfMin : undefined,
        max_cpf_ratio: cpfMax !== "::" ? cpfMax : undefined,
        meal_plan_type: step1Data.planType || undefined,
      };
      if (planName) body.meal_plan_name = planName;

      const apiResponse = await duplicateMealPlanTemplate(body);

      if (apiResponse.success) {
        const mealPlanId = apiResponse.data?.meal_plan_id;
        if (!mealPlanId) throw new Error("Meal plan created but ID not returned from server");

        navigate(`/client/${userId}/plans/meal/${mealPlanId}`, {
          state: { showSuccessToast: true },
          replace: false,
        });
      } else {
        throw new Error(apiResponse.message || "Failed to create meal plan from template");
      }
    } catch (err) {
      console.error("Error creating meal plan from template:", err);
      showError(
        "Failed to create meal plan from template",
        err instanceof Error ? err.message : "Please try again",
      );
    } finally {
      setIsCreating(false);
    }
  };

  const renderNutritionTag = (
    label: string,
    value: string,
    colors: { bg: string; border: string; text: string },
    mode: "recommendation" | "filter",
    isActive: boolean,
    onToggle: () => void,
  ) => {
    const clickable = mode === "filter";
    let style: React.CSSProperties;
    if (mode === "recommendation") {
      style = { backgroundColor: "#F9F9F9", border: "0.5px solid #8D8D8D", color: "#8D8D8D" };
    } else if (!isActive) {
      style = { backgroundColor: "#FFFFFF", border: "0.5px solid #646464", color: "#646464" };
    } else {
      style = { backgroundColor: colors.bg, border: `0.5px solid ${colors.border}`, color: colors.text };
    }
    return (
      <span
        onClick={clickable ? onToggle : undefined}
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${clickable ? "cursor-pointer select-none" : ""}`}
        style={style}
      >
        {label} : {value}
        {clickable && isActive && (
          <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
        )}
      </span>
    );
  };

  return (
    <div className="h-screen flex flex-col bg-[#F7F7FA] overflow-hidden">
      <ToastContainer toasts={toasts} onClose={removeToast} />

      <div className="flex-shrink-0">
        <PageHeader
          title={
            <div className="flex items-center gap-2">
              <span>
                {isGlobalTemplateMode
                  ? "Create template"
                  : isAdhocMealMode
                    ? "Create adhoc plan"
                    : "Create meal plan"}
              </span>
              <span className="px-2 py-0.5 rounded-full text-sm bg-[#E9E9E9] text-[#202020] font-semibold">
                Draft
              </span>
            </div>
          }
          breadcrumb={
            isGlobalTemplateMode
              ? "Global template > Create template"
              : isAdhocMealMode
                ? "Adhoc meal plan > Create meal plan"
                : "Clients > Log details > Plans > Create meal plan"
          }
          breadcrumbLinks={
            isGlobalTemplateMode
              ? ["/global-template", ""]
              : isAdhocMealMode
                ? ["/adhoc-meal", ""]
                : ["/client", `/client/${userId}`, `/client/${userId}/plans`, ""]
          }
        />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        {!isGlobalTemplateMode && !isAdhocMealMode && (
          <div className="flex-shrink-0">
            <UserDetailSection userId={userIdNumber} />
          </div>
        )}

        <div className="flex-1 flex flex-col overflow-hidden" style={{ minHeight: 0 }}>
          {/* Step indicator — on gray background */}
          <div className="flex-shrink-0 px-4 sm:px-6 lg:px-8 py-4">
            <MealPlanStepIndicator currentStep={currentStep} mode={stepIndicatorMode} />
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto custom-scrollbar-overlay px-4 sm:px-6 lg:px-8 pb-4">

              {/* ── Step 1: Define meal parameters ─────────────────────────────── */}
              {currentStep === 1 && (
                <div className="space-y-4">

                  {/* Adhoc mode — client contact details card */}
                  {isAdhocMealMode && (
                    <ClientContactSection
                      data={adhocContact}
                      onChange={setAdhocContact}
                      touchedFields={touchedFields}
                      onFieldTouched={(field) => markFieldTouched(`contact_${field}`)}
                    />
                  )}

                  <div className="bg-white rounded-md p-6 space-y-6">

                  {/* Plan name + Plan type + Category row (adhoc mode) */}
                  {isAdhocMealMode && (
                    <div className="grid grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm 3xl:text-base font-medium text-[#1F1F1F] mb-2">
                          Plan name
                        </label>
                        <input
                          type="text"
                          value={adhocPlanName}
                          onChange={(e) => {
                            setAdhocPlanName(e.target.value);
                            markFieldTouched("adhocPlanName");
                          }}
                          placeholder="Keto diet plan"
                          className={`w-full px-4 py-2 border rounded-md text-sm 3xl:text-base font-inter outline-none focus:border-[#0DB5B5]
                            ${touchedFields.adhocPlanName && !adhocPlanName.trim() ? "border-red-500" : "border-[#E0E0E0]"}
                            placeholder:text-[#9E9E9E]`}
                        />
                      </div>
                      <div>
                        <label className="block text-sm 3xl:text-base font-medium text-[#1F1F1F] mb-2">
                          Plan type
                        </label>
                        <SharedDropdown
                          options={planTypes.map((t) => ({ value: t, label: t }))}
                          value={step1Data.planType}
                          onChange={(type) => { setStep1Data({ ...step1Data, planType: type }); markFieldTouched("planType"); }}
                          placeholder={isPlanTypesLoading ? "Loading..." : "Regular"}
                          disabled={isPlanTypesLoading}
                          searchable={true}
                        />
                      </div>
                      <div>
                        <label className="block text-sm 3xl:text-base font-medium text-[#1F1F1F] mb-2">
                          Category
                        </label>
                        <SharedDropdown
                          options={adhocCategories.map((c) => ({ value: String(c.id), label: c.name }))}
                          value={adhocCategoryId != null ? String(adhocCategoryId) : ""}
                          onChange={(value) => {
                            setAdhocCategoryId(value ? parseInt(value, 10) : null);
                            markFieldTouched("adhocCategoryId");
                          }}
                          placeholder={isAdhocCategoriesLoading ? "Loading..." : "eg. Client"}
                          disabled={isAdhocCategoriesLoading}
                        />
                      </div>
                    </div>
                  )}

                  {/* Plan name + Plan type + Goal (global template mode only) */}
                  {isGlobalTemplateMode && (
                    <div className="grid grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-[#1F1F1F] mb-2">Plan name</label>
                        <input
                          type="text"
                          value={globalPlanName}
                          onChange={(e) => setGlobalPlanName(e.target.value)}
                          placeholder="Enter plan name"
                          className="w-full px-3 py-2 border border-[#E0E0E0] rounded-md text-sm font-inter font-normal placeholder:text-[#9E9E9E] outline-none focus:border-[#0DB5B5]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#1F1F1F] mb-2">Plan type</label>
                        <SharedDropdown
                          options={planTypes.map((t) => ({ value: t, label: t }))}
                          value={step1Data.planType}
                          onChange={(type) => { setStep1Data({ ...step1Data, planType: type }); markFieldTouched("planType"); }}
                          placeholder={isPlanTypesLoading ? "Loading..." : "Select plan type"}
                          disabled={isPlanTypesLoading}
                          searchable={true}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#1F1F1F] mb-2">Goal</label>
                        <SharedDropdown
                          options={GLOBAL_GOALS.map((g) => ({ value: g, label: g }))}
                          value={globalGoal}
                          onChange={setGlobalGoal}
                          placeholder="Select goal"
                        />
                      </div>
                    </div>
                  )}

                  {/* Plan Type (client mode only — adhoc renders its own row above) */}
                  {!isGlobalTemplateMode && !isAdhocMealMode && (
                  <div>
                    <label className="block text-sm 3xl:text-base font-medium text-[#1F1F1F] mb-2">
                      Plan type
                    </label>
                    <div className="relative max-w-xs" ref={planTypeDropdownRef}>
                      <input
                        type="text"
                        value={planTypeSearchTerm}
                        onChange={(e) => {
                          setPlanTypeSearchTerm(e.target.value);
                          setShowPlanTypeDropdown(true);
                          markFieldTouched("planType");
                        }}
                        onFocus={() => setShowPlanTypeDropdown(true)}
                        placeholder={isPlanTypesLoading ? "Loading plan types..." : "Search plan type"}
                        disabled={isPlanTypesLoading}
                        className={`w-full px-4 py-2 border rounded-md text-sm 3xl:text-base font-inter outline-none focus:border-[#0DB5B5]
                          ${shouldShowEmptyError("planType", step1Data.planType) ? "border-red-500" : "border-[#E0E0E0]"}
                          ${isPlanTypesLoading ? "bg-gray-50 cursor-not-allowed" : ""}`}
                      />
                      {isPlanTypesLoading && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <div className="w-4 h-4 border-2 border-[#0DB5B5] border-t-transparent rounded-full animate-spin" />
                        </div>
                      )}
                      {showPlanTypeDropdown && filteredPlanTypes.length > 0 && !isPlanTypesLoading && (
                        <div
                          className="fixed bg-white border border-[#E5E5EA] rounded-md shadow-lg max-h-60 overflow-y-auto custom-scrollbar-overlay z-[60]"
                          style={{
                            top: planTypeDropdownRef.current?.getBoundingClientRect().bottom! + 4,
                            left: planTypeDropdownRef.current?.getBoundingClientRect().left,
                            width: planTypeDropdownRef.current?.getBoundingClientRect().width,
                          }}
                        >
                          {filteredPlanTypes.map((type) => (
                            <div
                              key={type}
                              onClick={() => {
                                setStep1Data({ ...step1Data, planType: type });
                                setPlanTypeSearchTerm(type);
                                setShowPlanTypeDropdown(false);
                                markFieldTouched("planType");
                              }}
                              className="m-2 px-2 py-1.5 hover:bg-[#E5F8F8] rounded-md cursor-pointer text-sm 3xl:text-base"
                            >
                              {type}
                            </div>
                          ))}
                        </div>
                      )}
                      {showPlanTypeDropdown && !isPlanTypesLoading && planTypeSearchTerm.length > 0 && filteredPlanTypes.length === 0 && (
                        <div
                          className="fixed bg-white border border-[#E5E5EA] rounded-md shadow-lg z-[60]"
                          style={{
                            top: planTypeDropdownRef.current?.getBoundingClientRect().bottom! + 4,
                            left: planTypeDropdownRef.current?.getBoundingClientRect().left,
                            width: planTypeDropdownRef.current?.getBoundingClientRect().width,
                          }}
                        >
                          <p className="px-4 py-3 text-sm 3xl:text-base text-[#8E8E93]">No plan types found</p>
                        </div>
                      )}
                    </div>
                    {shouldShowEmptyError("planType", step1Data.planType) && (
                      <p className="mt-1 text-xs 3xl:text-sm text-red-500">This field is required</p>
                    )}
                  </div>
                  )}

                  {/* Grid for parameters */}
                  <div className="grid grid-cols-3 gap-6">
                    {/* Total Calories */}
                    <div>
                      <label className="block text-sm 3xl:text-base font-medium text-[#1F1F1F] mb-2">
                        Total calories <span className="text-[#8E8E93] font-normal">(kcals)</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          placeholder="Min"
                          value={step1Data.totalCalories.min}
                          onChange={(e) => {
                            if (!isCaloriesValueValid(e.target.value)) return;
                            setStep1Data({ ...step1Data, totalCalories: { ...step1Data.totalCalories, min: e.target.value } });
                            markFieldTouched("totalCalories");
                          }}
                          onBlur={() => markFieldTouched("totalCalories")}
                          className={`w-full px-4 py-2 border ${shouldShowEmptyError("totalCalories", step1Data.totalCalories.min) || shouldShowError("totalCalories") ? "border-red-500" : "border-[#E0E0E0]"} rounded-md text-sm 3xl:text-base font-inter focus:outline-none focus:border-[#0DB5B5]`}
                        />
                        <span className="text-[#8E8E93]">to</span>
                        <input
                          type="number"
                          placeholder="Max"
                          value={step1Data.totalCalories.max}
                          onChange={(e) => {
                            if (!isCaloriesValueValid(e.target.value)) return;
                            setStep1Data({ ...step1Data, totalCalories: { ...step1Data.totalCalories, max: e.target.value } });
                            markFieldTouched("totalCalories");
                          }}
                          onBlur={() => markFieldTouched("totalCalories")}
                          className={`w-full px-4 py-2 border ${shouldShowEmptyError("totalCalories", step1Data.totalCalories.max) || shouldShowError("totalCalories") ? "border-red-500" : "border-[#E0E0E0]"} rounded-md text-sm 3xl:text-base font-inter focus:outline-none focus:border-[#0DB5B5]`}
                        />
                      </div>
                      {shouldShowError("totalCalories") && <p className="mt-1 text-xs 3xl:text-sm text-red-500">{validationErrors.totalCalories}</p>}
                      {!shouldShowError("totalCalories") && touchedFields.totalCalories && (isFieldEmpty(step1Data.totalCalories.min) || isFieldEmpty(step1Data.totalCalories.max)) && (
                        <p className="mt-1 text-xs 3xl:text-sm text-red-500">Both min and max values are required</p>
                      )}
                      <p className="mt-1 text-xs 3xl:text-sm text-[#8E8E93]">Max 4 digits (up to 9999)</p>
                    </div>

                    {/* Total Adj.Protein */}
                    <div>
                      <label className="block text-sm 3xl:text-base font-medium text-[#1F1F1F] mb-2">
                        Total Adj.Protein <span className="text-[#8E8E93] font-normal">(g)</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number" placeholder="Min" value={step1Data.totalAdjProtein.min}
                          onChange={(e) => { setStep1Data({ ...step1Data, totalAdjProtein: { ...step1Data.totalAdjProtein, min: e.target.value } }); markFieldTouched("totalAdjProtein"); }}
                          onBlur={() => markFieldTouched("totalAdjProtein")}
                          className={`w-full px-4 py-2 border ${shouldShowEmptyError("totalAdjProtein", step1Data.totalAdjProtein.min) || shouldShowError("totalAdjProtein") ? "border-red-500" : "border-[#E0E0E0]"} rounded-md text-sm 3xl:text-base font-inter focus:outline-none focus:border-[#0DB5B5]`}
                        />
                        <span className="text-[#8E8E93]">to</span>
                        <input
                          type="number" placeholder="Max" value={step1Data.totalAdjProtein.max}
                          onChange={(e) => { setStep1Data({ ...step1Data, totalAdjProtein: { ...step1Data.totalAdjProtein, max: e.target.value } }); markFieldTouched("totalAdjProtein"); }}
                          onBlur={() => markFieldTouched("totalAdjProtein")}
                          className={`w-full px-4 py-2 border ${shouldShowEmptyError("totalAdjProtein", step1Data.totalAdjProtein.max) || shouldShowError("totalAdjProtein") ? "border-red-500" : "border-[#E0E0E0]"} rounded-md text-sm 3xl:text-base font-inter focus:outline-none focus:border-[#0DB5B5]`}
                        />
                      </div>
                      {shouldShowError("totalAdjProtein") && <p className="mt-1 text-xs 3xl:text-sm text-red-500">{validationErrors.totalAdjProtein}</p>}
                      {!shouldShowError("totalAdjProtein") && touchedFields.totalAdjProtein && (isFieldEmpty(step1Data.totalAdjProtein.min) || isFieldEmpty(step1Data.totalAdjProtein.max)) && (
                        <p className="mt-1 text-xs 3xl:text-sm text-red-500">Both min and max values are required</p>
                      )}
                    </div>

                    {/* Total Net Carbs */}
                    <div>
                      <label className="block text-sm 3xl:text-base font-medium text-[#1F1F1F] mb-2">
                        Total Net Carbs <span className="text-[#8E8E93] font-normal">(g)</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number" placeholder="Min" value={step1Data.totalNetCarbs.min}
                          onChange={(e) => { setStep1Data({ ...step1Data, totalNetCarbs: { ...step1Data.totalNetCarbs, min: e.target.value } }); markFieldTouched("totalNetCarbs"); }}
                          onBlur={() => markFieldTouched("totalNetCarbs")}
                          className={`w-full px-4 py-2 border ${shouldShowEmptyError("totalNetCarbs", step1Data.totalNetCarbs.min) || shouldShowError("totalNetCarbs") ? "border-red-500" : "border-[#E0E0E0]"} rounded-md text-sm 3xl:text-base font-inter focus:outline-none focus:border-[#0DB5B5]`}
                        />
                        <span className="text-[#8E8E93]">to</span>
                        <input
                          type="number" placeholder="Max" value={step1Data.totalNetCarbs.max}
                          onChange={(e) => { setStep1Data({ ...step1Data, totalNetCarbs: { ...step1Data.totalNetCarbs, max: e.target.value } }); markFieldTouched("totalNetCarbs"); }}
                          onBlur={() => markFieldTouched("totalNetCarbs")}
                          className={`w-full px-4 py-2 border ${shouldShowEmptyError("totalNetCarbs", step1Data.totalNetCarbs.max) || shouldShowError("totalNetCarbs") ? "border-red-500" : "border-[#E0E0E0]"} rounded-md text-sm 3xl:text-base font-inter focus:outline-none focus:border-[#0DB5B5]`}
                        />
                      </div>
                      {shouldShowError("totalNetCarbs") && <p className="mt-1 text-xs 3xl:text-sm text-red-500">{validationErrors.totalNetCarbs}</p>}
                      {!shouldShowError("totalNetCarbs") && touchedFields.totalNetCarbs && (isFieldEmpty(step1Data.totalNetCarbs.min) || isFieldEmpty(step1Data.totalNetCarbs.max)) && (
                        <p className="mt-1 text-xs 3xl:text-sm text-red-500">Both min and max values are required</p>
                      )}
                    </div>

                    {/* Total Fats */}
                    <div>
                      <label className="block text-sm 3xl:text-base font-medium text-[#1F1F1F] mb-2">
                        Total fats <span className="text-[#8E8E93] font-normal">(g)</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number" placeholder="Min" value={step1Data.totalFats.min}
                          onChange={(e) => { setStep1Data({ ...step1Data, totalFats: { ...step1Data.totalFats, min: e.target.value } }); markFieldTouched("totalFats"); }}
                          onBlur={() => markFieldTouched("totalFats")}
                          className={`w-full px-4 py-2 border ${shouldShowEmptyError("totalFats", step1Data.totalFats.min) || shouldShowError("totalFats") ? "border-red-500" : "border-[#E0E0E0]"} rounded-md text-sm 3xl:text-base font-inter focus:outline-none focus:border-[#0DB5B5]`}
                        />
                        <span className="text-[#8E8E93]">to</span>
                        <input
                          type="number" placeholder="Max" value={step1Data.totalFats.max}
                          onChange={(e) => { setStep1Data({ ...step1Data, totalFats: { ...step1Data.totalFats, max: e.target.value } }); markFieldTouched("totalFats"); }}
                          onBlur={() => markFieldTouched("totalFats")}
                          className={`w-full px-4 py-2 border ${shouldShowEmptyError("totalFats", step1Data.totalFats.max) || shouldShowError("totalFats") ? "border-red-500" : "border-[#E0E0E0]"} rounded-md text-sm 3xl:text-base font-inter focus:outline-none focus:border-[#0DB5B5]`}
                        />
                      </div>
                      {shouldShowError("totalFats") && <p className="mt-1 text-xs 3xl:text-sm text-red-500">{validationErrors.totalFats}</p>}
                      {!shouldShowError("totalFats") && touchedFields.totalFats && (isFieldEmpty(step1Data.totalFats.min) || isFieldEmpty(step1Data.totalFats.max)) && (
                        <p className="mt-1 text-xs 3xl:text-sm text-red-500">Both min and max values are required</p>
                      )}
                    </div>

                    {/* Total GL */}
                    <div>
                      <label className="block text-sm 3xl:text-base font-medium text-[#1F1F1F] mb-2">
                        Total GL
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number" placeholder="Min" value={step1Data.totalGL.min}
                          onChange={(e) => { setStep1Data({ ...step1Data, totalGL: { ...step1Data.totalGL, min: e.target.value } }); markFieldTouched("totalGL"); }}
                          onBlur={() => markFieldTouched("totalGL")}
                          className={`w-full px-4 py-2 border ${shouldShowEmptyError("totalGL", step1Data.totalGL.min) || shouldShowError("totalGL") ? "border-red-500" : "border-[#E0E0E0]"} rounded-md text-sm 3xl:text-base font-inter focus:outline-none focus:border-[#0DB5B5]`}
                        />
                        <span className="text-[#8E8E93]">to</span>
                        <input
                          type="number" placeholder="Max" value={step1Data.totalGL.max}
                          onChange={(e) => { setStep1Data({ ...step1Data, totalGL: { ...step1Data.totalGL, max: e.target.value } }); markFieldTouched("totalGL"); }}
                          onBlur={() => markFieldTouched("totalGL")}
                          className={`w-full px-4 py-2 border ${shouldShowEmptyError("totalGL", step1Data.totalGL.max) || shouldShowError("totalGL") ? "border-red-500" : "border-[#E0E0E0]"} rounded-md text-sm 3xl:text-base font-inter focus:outline-none focus:border-[#0DB5B5]`}
                        />
                      </div>
                      {shouldShowError("totalGL") && <p className="mt-1 text-xs 3xl:text-sm text-red-500">{validationErrors.totalGL}</p>}
                      {!shouldShowError("totalGL") && touchedFields.totalGL && (isFieldEmpty(step1Data.totalGL.min) || isFieldEmpty(step1Data.totalGL.max)) && (
                        <p className="mt-1 text-xs 3xl:text-sm text-red-500">Both min and max values are required</p>
                      )}
                    </div>

                    {/* C:P:F Ratio */}
                    <div>
                      <label className="text-sm 3xl:text-base font-medium text-[#1F1F1F] mb-2 flex items-center justify-between gap-1">
                        C:P:F ratio
                        <Info className="w-4 h-4 text-[#8E8E93]" />
                      </label>
                      <div className="flex items-center gap-2">
                        <div className="w-full px-4 py-2 border border-[#E0E0E0] rounded-md text-sm 3xl:text-base font-inter bg-gray-50 text-[#8E8E93]">
                          {step1Data.cpfRatio.min.c} : {step1Data.cpfRatio.min.p} : {step1Data.cpfRatio.min.f}
                        </div>
                        <span className="text-[#8E8E93]">to</span>
                        <div className="w-full px-4 py-2 border border-[#E0E0E0] rounded-md text-sm 3xl:text-base font-inter bg-gray-50 text-[#8E8E93]">
                          {step1Data.cpfRatio.max.c} : {step1Data.cpfRatio.max.p} : {step1Data.cpfRatio.max.f}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Add Instructions */}
                  {!showInstructions ? (
                    <div>
                      <button
                        onClick={() => setShowInstructions(true)}
                        className="flex items-center gap-2 text-[#0DB5B5] text-sm 3xl:text-base font-medium hover:text-[#0CA0A0] transition-colors"
                      >
                        <span className="text-xl">+</span>
                        Add instructions
                      </button>
                      <p className="text-xs 3xl:text-sm text-[#8E8E93] mt-1">
                        Add guidance to help the client follow this meal plan effectively.
                      </p>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm 3xl:text-base font-medium text-[#1F1F1F]">
                          Add <span className="text-[#8E8E93] font-normal">(Optional)</span>
                        </label>
                        {showDeleteInstructionsConfirm ? (
                          <div className="flex items-center gap-2">
                            <span className="text-sm 3xl:text-base text-[#1F1F1F]">Delete instructions?</span>
                            <Button
                              variant="danger"
                              size="xs"
                              onClick={() => {
                                setShowInstructions(false);
                                setStep1Data({ ...step1Data, instructions: "" });
                                setShowDeleteInstructionsConfirm(false);
                              }}
                            >
                              Confirm delete
                            </Button>
                            <button
                              onClick={() => setShowDeleteInstructionsConfirm(false)}
                              className="text-[#8E8E93] hover:text-[#1F1F1F] transition-colors p-0.5 rounded border-[0.5px] border-[#1F1F1F]"
                            >
                              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setShowDeleteInstructionsConfirm(true)}
                            className="text-[#FF3B30] hover:text-[#E63228] transition-colors"
                          >
                            <DeleteIcon className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                      <textarea
                        placeholder="e.g., Avoid fried foods, prefer steaming, complete meal within 30 minutes"
                        value={step1Data.instructions}
                        onChange={(e) => setStep1Data({ ...step1Data, instructions: e.target.value })}
                        className="w-full px-4 py-3 border border-[#E0E0E0] rounded-md text-sm 3xl:text-base font-inter focus:outline-none focus:border-[#0DB5B5] resize-none"
                        rows={4}
                      />
                    </div>
                  )}
                  </div>
                </div>
              )}

              {/* ── Step 2: How do you want to start? ──────────────────────────── */}
              {currentStep === 2 && (
                <div className="grid grid-cols-3 gap-4">
                  {step2Cards.map((card) => (
                    <div
                      key={card.id}
                      onClick={() => setStep2Selection(card.id)}
                      className={`px-5 py-3 border rounded-lg cursor-pointer transition-all flex flex-col
                        ${step2Selection === card.id
                          ? "border-[#E0E0E0] bg-[#F0FAFA]"
                          : "border-[#E0E0E0] bg-white hover:border-[#0DB5B5] hover:shadow-sm"
                        }`}
                    >
                      {/* Icon row with inline badge */}
                      <div className="flex items-center justify-between mb-1">
                        <div className="w-8 h-8 flex items-center justify-center">
                          {step2Selection === card.id ? card.activeIcon : card.icon}
                        </div>
                        {card.badge && (
                          <span className="px-2 py-0.5 bg-[#0DB5B5] text-white text-[10px] rounded-full font-medium">
                            {card.badge}
                          </span>
                        )}
                      </div>

                      {/* Title & description */}
                      <div className="flex-1">
                        <h3 className="font-medium text-[14px] 3xl:text-base  text-[#1F1F1F]">
                          {card.title}
                        </h3>
                        <p className="text-[#8D8D8D] text-[12px] 3xl:text-sm leading-relaxed">
                          {card.description}
                        </p>
                      </div>

                      {/* Footer info — extra top spacing */}
                      <p className={`text-[12px] 3xl:text-[14px] mt-8 ${step2Selection === card.id ? "text-[#0DB5B5]" : "text-[#8D8D8D]"}`}>
                        {card.footer}
                      </p>

                    </div>
                  ))}
                </div>
              )}

              {/* ── Step 3: Based on selection ─────────────────────────────────── */}
              {currentStep === 3 && (
                <>
                  {step2Selection === "scratch" ? (
                    /* Start from scratch: full meal timing form */
                    <div className="bg-white rounded-md p-6">
                      <h3 className="text-base font-semibold text-[#1F1F1F] mb-4">
                        Meal timing &amp; schedule
                      </h3>
                      <div className="grid grid-cols-6 gap-6">
                        {/* Left: meal list */}
                        <div className="col-span-4 bg-[#FBFBFB] rounded-md p-4">
                          {mealTimings.length === 0 ? (
                            <div className="flex items-center justify-center h-52">
                              <div className="text-center">
                                <p className="text-[#828282] text-sm 3xl:text-base">No meals scheduled yet</p>
                                <p className="text-[#8E8E93] text-sm 3xl:text-base mt-1">Add meal timing using the form on the right.</p>
                              </div>
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 gap-4">
                              {sortedMealTimings.map((meal) => (
                                <MealCard
                                  key={meal.id}
                                  meal={meal}
                                  isSelected={selectedMealId === meal.id}
                                  showMenu={mealTimings.length > 1}
                                  onSelect={() => handleSelectMeal(meal)}
                                  onDelete={() => handleDeleteMeal(meal.id)}
                                  formatTime={formatTime}
                                />
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Right: add/edit form */}
                        <div className="col-span-2 border-[0.5px] border-[#E0E0E0] rounded-md p-4">
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm 3xl:text-base font-medium text-[#1F1F1F] mb-2">Meal type</label>
                              {selectedMealId ? (
                                <div className="w-full px-4 py-2 border border-[#E0E0E0] rounded-md text-sm 3xl:text-base font-inter bg-gray-50 text-[#202020]">
                                  {currentMeal.mealType}
                                </div>
                              ) : (
                                <SharedDropdown
                                  value={currentMeal.mealType}
                                  placeholder="Select"
                                  options={toOptions(availableMealTypes)}
                                  onChange={(val) => setCurrentMeal({ ...currentMeal, mealType: val })}
                                />
                              )}
                            </div>

                            <div>
                              <label className="block text-sm 3xl:text-base font-medium text-[#1F1F1F] mb-2">Time</label>
                              <div className="flex items-center gap-2">
                                <SharedDropdown
                                  className="flex-1"
                                  value={currentMeal.hour !== "Hr" ? currentMeal.hour : ""}
                                  placeholder="Hr"
                                  options={toOptions(hours)}
                                  onChange={(val) => setCurrentMeal({ ...currentMeal, hour: val })}
                                />
                                <SharedDropdown
                                  className="flex-1"
                                  value={currentMeal.minute !== "Min" ? currentMeal.minute : ""}
                                  placeholder="Min"
                                  options={toOptions(minutes)}
                                  onChange={(val) => setCurrentMeal({ ...currentMeal, minute: val })}
                                />
                                <SharedDropdown
                                  className="flex-1"
                                  value={currentMeal.period}
                                  placeholder="AM"
                                  options={toOptions(["AM", "PM"])}
                                  onChange={(val) => setCurrentMeal({ ...currentMeal, period: val })}
                                />
                              </div>
                            </div>

                            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                              <Button
                                variant="ghost"
                                onClick={handleReset}
                              >
                                Reset
                              </Button>
                              <Button
                                variant="primary"
                                size="md"
                                onClick={handleAddOrUpdateMeal}
                                disabled={!currentMeal.mealType || currentMeal.hour === "Hr" || currentMeal.minute === "Min"}
                              >
                                {selectedMealId ? "Update" : "Add"}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : step2Selection === "template" ? (
                    /* Browse global meal plan templates */
                    <div className="bg-white rounded-md p-6 space-y-4">
                      {/* Header + Search + Nutrition tags */}
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="text-base font-semibold text-[#1F1F1F]">Browse global meal plans</h3>
                          <p className="text-xs text-[#8E8E93] mt-0.5">Read-only · Duplicate to create your own editable copy</p>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap justify-end flex-shrink-0">
                          {/* <div className="relative flex-shrink-0">
                            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8E8E93]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                              type="text"
                              placeholder="Search.."
                              value={templateSearch}
                              onChange={(e) => setTemplateSearch(e.target.value)}
                              style={{ border: "0.5px solid #D9D9D9" }}
                              className="pl-9 pr-4 py-1.5 rounded-md text-xs bg-white focus:outline-none w-44"
                            />
                          </div> */}
                          {step1Data.totalCalories.min && step1Data.totalCalories.max && renderNutritionTag(
                            "Cal",
                            `${step1Data.totalCalories.min}-${step1Data.totalCalories.max}`,
                            { bg: "rgba(180,74,230,0.08)", border: "#B44AE6", text: "#B44AE6" },
                            templateFilterMode,
                            activeTemplateTags.cal,
                            () => setActiveTemplateTags((s) => ({ ...s, cal: !s.cal })),
                          )}
                          {step1Data.totalAdjProtein.min && step1Data.totalAdjProtein.max && renderNutritionTag(
                            "A.pro",
                            `${step1Data.totalAdjProtein.min}-${step1Data.totalAdjProtein.max}g`,
                            { bg: "rgba(107,189,250,0.1)", border: "#6BBDFA", text: "#6BBDFA" },
                            templateFilterMode,
                            activeTemplateTags.protein,
                            () => setActiveTemplateTags((s) => ({ ...s, protein: !s.protein })),
                          )}
                          {step1Data.totalNetCarbs.min && step1Data.totalNetCarbs.max && renderNutritionTag(
                            "Carb",
                            `${step1Data.totalNetCarbs.min}-${step1Data.totalNetCarbs.max}g`,
                            { bg: "rgba(255,181,50,0.1)", border: "#FFB532", text: "#FFB532" },
                            templateFilterMode,
                            activeTemplateTags.carb,
                            () => setActiveTemplateTags((s) => ({ ...s, carb: !s.carb })),
                          )}
                          {step1Data.totalFats.min && step1Data.totalFats.max && renderNutritionTag(
                            "Fat",
                            `${step1Data.totalFats.min}-${step1Data.totalFats.max}g`,
                            { bg: "rgba(242,91,236,0.08)", border: "#F25BEC", text: "#F25BEC" },
                            templateFilterMode,
                            activeTemplateTags.fat,
                            () => setActiveTemplateTags((s) => ({ ...s, fat: !s.fat })),
                          )}
                          {step1Data.totalGL.min && step1Data.totalGL.max && renderNutritionTag(
                            "GL",
                            `${step1Data.totalGL.min}-${step1Data.totalGL.max}`,
                            { bg: "rgba(160,202,7,0.1)", border: "#A0CA07", text: "#A0CA07" },
                            templateFilterMode,
                            activeTemplateTags.gl,
                            () => setActiveTemplateTags((s) => ({ ...s, gl: !s.gl })),
                          )}
                        </div>
                      </div>

                      {/* Filters */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <div
                          className="inline-flex items-center rounded-full p-1"
                          style={{ backgroundColor: "#1F1F1F", border: "0.5px solid #1F1F1F" }}
                        >
                          <button
                            type="button"
                            onClick={() => handleTemplateModeChange("recommendation")}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                              templateFilterMode === "recommendation"
                                ? "bg-white text-[#1F1F1F]"
                                : "bg-transparent text-[#E8E8E8]"
                            }`}
                          >
                            {templateFilterMode === "recommendation"
                              ? <RecommendationActiveIcon className="w-3.5 h-3.5" />
                              : <RecommendationIcon className="w-3.5 h-3.5" />}
                            <span>Recommendation</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleTemplateModeChange("filter")}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                              templateFilterMode === "filter"
                                ? "bg-white text-[#1F1F1F]"
                                : "bg-transparent text-[#E8E8E8]"
                            }`}
                          >
                            {templateFilterMode === "filter"
                              ? <FilterActiveIcon className="w-3.5 h-3.5" />
                              : <FilterIcon className="w-3.5 h-3.5" />}
                            <span>Filter</span>
                          </button>
                        </div>
                        <span className="text-[#D9D9D9] mx-1">|</span>
                        <div className="w-[136px]">
                          <FilterDropdown
                            value={templatePlanTypeFilter}
                            placeholder="Plan type"
                            options={Array.from(new Set(GLOBAL_TEMPLATES.map((t) => t.planType)))}
                            onChange={(val) => setTemplatePlanTypeFilter(val as string)}
                          />
                        </div>
                        <div className="w-[150px]">
                          <FilterDropdown
                            value={templateOptionsFilter}
                            placeholder="Select options"
                            options={Array.from({ length: 6 }, (_, i) => `${i + 1} options/meal`)}
                            onChange={(val) => setTemplateOptionsFilter(val as string)}
                          />
                        </div>
                        <div className="w-[150px]">
                          <FilterDropdown
                            value={templateMealTypeFilter}
                            placeholder="Select no. of meal"
                            options={Array.from({ length: 13 }, (_, i) => `${i + 1} meals/day`)}
                            onChange={(val) => setTemplateMealTypeFilter(val as string)}
                          />
                        </div>
                        <div className="w-[150px]">
                          <FilterDropdown
                            value={templateGoalFilter}
                            placeholder="Select goal"
                            options={GLOBAL_GOALS}
                            onChange={(val) => setTemplateGoalFilter(val as string)}
                          />
                        </div>
                      </div>

                      {/* Template cards grid */}
                      {isLoadingTemplates ? (
                        <div className="bg-white rounded-md p-4 flex items-center justify-center h-52">
                          <div className="flex flex-col items-center gap-2">
                            <svg className="w-6 h-6 text-[#0DB5B5] animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                            </svg>
                            <p className="text-[#8E8E93] text-sm">Loading templates…</p>
                          </div>
                        </div>
                      ) : filteredTemplates.length === 0 ? (
                        <div className="bg-white rounded-md p-4 flex items-center justify-center h-52">
                          <p className="text-[#8E8E93] text-sm">No templates found matching your criteria.</p>
                        </div>
                      ) : (
                        <>
                          <div className="grid grid-cols-2 xl:grid-cols-3 3xl:grid-cols-4 gap-4">
                            {(() => {
                              const maxMatch = Math.max(...filteredTemplates.map((t) => t.matchPercent));
                              return filteredTemplates.map((template) => (
                                <PlanCard
                                  key={template.id}
                                  plan={template}
                                  isTopMatch={template.matchPercent === maxMatch}
                                  isCreating={isCreating}
                                  showMatch={templateFilterMode === "recommendation"}
                                  onPreview={() => handlePreviewTemplate(template)}
                                  onUse={() => handleDuplicateTemplate(template)}
                                />
                              ));
                            })()}
                          </div>
                          <Pagination
                            currentPage={templatePage}
                            totalPages={Math.max(1, Math.ceil(templateTotal / TEMPLATE_PAGE_SIZE))}
                            onPageChange={setTemplatePage}
                            resultCount={templateTotal}
                          />
                        </>
                      )}
                    </div>
                  ) : (
                    /* Copy from assigned client — browser */
                    <div className="bg-white rounded-md p-6 space-y-4">
                      {/* Header + Search + Nutrition tags */}
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="text-base font-semibold text-[#1F1F1F]">Browse meal plans from client</h3>
                          <p className="text-xs text-[#8E8E93] mt-0.5">Read-only · Duplicate to create your own editable copy</p>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap justify-end flex-shrink-0">
                          {/* <div className="relative flex-shrink-0">
                            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8E8E93]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                              type="text"
                              placeholder="Search client.."
                              value={clientSearch}
                              onChange={(e) => setClientSearch(e.target.value)}
                              style={{ border: "0.5px solid #D9D9D9" }}
                              className="pl-9 pr-4 py-1.5 rounded-md text-xs bg-white focus:outline-none w-44"
                            />
                          </div> */}
                          {step1Data.totalCalories.min && step1Data.totalCalories.max && renderNutritionTag(
                            "Cal",
                            `${step1Data.totalCalories.min}-${step1Data.totalCalories.max}`,
                            { bg: "rgba(180,74,230,0.08)", border: "#B44AE6", text: "#B44AE6" },
                            clientFilterMode,
                            activeClientTags.cal,
                            () => setActiveClientTags((s) => ({ ...s, cal: !s.cal })),
                          )}
                          {step1Data.totalAdjProtein.min && step1Data.totalAdjProtein.max && renderNutritionTag(
                            "A.pro",
                            `${step1Data.totalAdjProtein.min}-${step1Data.totalAdjProtein.max}g`,
                            { bg: "rgba(107,189,250,0.1)", border: "#6BBDFA", text: "#6BBDFA" },
                            clientFilterMode,
                            activeClientTags.protein,
                            () => setActiveClientTags((s) => ({ ...s, protein: !s.protein })),
                          )}
                          {step1Data.totalNetCarbs.min && step1Data.totalNetCarbs.max && renderNutritionTag(
                            "Carb",
                            `${step1Data.totalNetCarbs.min}-${step1Data.totalNetCarbs.max}g`,
                            { bg: "rgba(255,181,50,0.1)", border: "#FFB532", text: "#FFB532" },
                            clientFilterMode,
                            activeClientTags.carb,
                            () => setActiveClientTags((s) => ({ ...s, carb: !s.carb })),
                          )}
                          {step1Data.totalFats.min && step1Data.totalFats.max && renderNutritionTag(
                            "Fat",
                            `${step1Data.totalFats.min}-${step1Data.totalFats.max}g`,
                            { bg: "rgba(242,91,236,0.08)", border: "#F25BEC", text: "#F25BEC" },
                            clientFilterMode,
                            activeClientTags.fat,
                            () => setActiveClientTags((s) => ({ ...s, fat: !s.fat })),
                          )}
                          {step1Data.totalGL.min && step1Data.totalGL.max && renderNutritionTag(
                            "GL",
                            `${step1Data.totalGL.min}-${step1Data.totalGL.max}`,
                            { bg: "rgba(160,202,7,0.1)", border: "#A0CA07", text: "#A0CA07" },
                            clientFilterMode,
                            activeClientTags.gl,
                            () => setActiveClientTags((s) => ({ ...s, gl: !s.gl })),
                          )}
                        </div>
                      </div>

                      {/* Filters */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <div
                          className="inline-flex items-center rounded-full p-1"
                          style={{ backgroundColor: "#1F1F1F", border: "0.5px solid #1F1F1F" }}
                        >
                          <button
                            type="button"
                            onClick={() => handleClientModeChange("recommendation")}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                              clientFilterMode === "recommendation"
                                ? "bg-white text-[#1F1F1F]"
                                : "bg-transparent text-[#E8E8E8]"
                            }`}
                          >
                            {clientFilterMode === "recommendation"
                              ? <RecommendationActiveIcon className="w-3.5 h-3.5" />
                              : <RecommendationIcon className="w-3.5 h-3.5" />}
                            <span>Recommendation</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleClientModeChange("filter")}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                              clientFilterMode === "filter"
                                ? "bg-white text-[#1F1F1F]"
                                : "bg-transparent text-[#E8E8E8]"
                            }`}
                          >
                            {clientFilterMode === "filter"
                              ? <FilterActiveIcon className="w-3.5 h-3.5" />
                              : <FilterIcon className="w-3.5 h-3.5" />}
                            <span>Filter</span>
                          </button>
                        </div>
                        <span className="text-[#D9D9D9] mx-1">|</span>
                        <div className="w-[136px]">
                          <FilterDropdown
                            value={clientNutritionistFilter}
                            placeholder="Nutritionist"
                            options={nutritionistsList.map((n) => n.name)}
                            onChange={(val) => setClientNutritionistFilter(val as string[])}
                            multiSelect={true}
                          />
                        </div>
                        <div className="w-[136px]">
                          <FilterDropdown
                            value={clientPlanTypeFilter}
                            placeholder="Plan type"
                            options={planTypes}
                            onChange={(val) => setClientPlanTypeFilter(val as string)}
                          />
                        </div>
                        <div className="w-[150px]">
                          <FilterDropdown
                            value={clientOptionsFilter}
                            placeholder="Select options"
                            options={Array.from({ length: 6 }, (_, i) => `${i + 1} options/meal`)}
                            onChange={(val) => setClientOptionsFilter(val as string)}
                          />
                        </div>
                        <div className="w-[150px]">
                          <FilterDropdown
                            value={clientMealsFilter}
                            placeholder="Select no. of meal"
                            options={Array.from({ length: 13 }, (_, i) => `${i + 1} meals/day`)}
                            onChange={(val) => setClientMealsFilter(val as string)}
                          />
                        </div>
                        <div className="w-[150px]">
                          <FilterDropdown
                            value={clientNameFilter}
                            placeholder="Select client"
                            options={clientNameOptions}
                            onChange={(val) => setClientNameFilter(val as string)}
                            searchable={true}
                          />
                        </div>
                        <div className="w-[150px]">
                          <FilterDropdown
                            value={clientGoalFilter}
                            placeholder="Select goal"
                            options={GLOBAL_GOALS}
                            onChange={(val) => setClientGoalFilter(val as string)}
                          />
                        </div>
                      </div>

                      {/* Client groups */}
                      {isClientPlansLoading ? (
                        <div className="bg-white rounded-md p-4 flex items-center justify-center h-52">
                          <div className="flex flex-col items-center gap-2">
                            <svg className="w-6 h-6 text-[#0DB5B5] animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                            </svg>
                            <p className="text-[#8E8E93] text-sm">Loading plans…</p>
                          </div>
                        </div>
                      ) : filteredClientPlans.length === 0 ? (
                        <div className="bg-white rounded-md p-4 flex items-center justify-center h-52">
                          <p className="text-[#8E8E93] text-sm">No plans found matching your criteria.</p>
                        </div>
                      ) : (
                        <>
                          <div className="grid grid-cols-2 xl:grid-cols-3 3xl:grid-cols-4 gap-4">
                            {(() => {
                              const maxMatch = Math.max(...filteredClientPlans.map((p) => p.matchPercent));
                              return filteredClientPlans.map((plan) => (
                                <PlanCard
                                  key={plan.id}
                                  plan={plan}
                                  isTopMatch={plan.matchPercent === maxMatch}
                                  isCreating={isCreating}
                                  showMatch={clientFilterMode === "recommendation"}
                                  onPreview={() => handlePreviewClientPlan(plan)}
                                  onUse={() => handleDuplicatePlan(plan)}
                                />
                              ));
                            })()}
                          </div>
                          <Pagination
                            currentPage={clientPage}
                            totalPages={Math.max(1, Math.ceil(clientTotal / CLIENT_PAGE_SIZE))}
                            onPageChange={setClientPage}
                            resultCount={clientTotal}
                          />
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* ── Footer ─────────────────────────────────────────────────────── */}
            <div className="flex-shrink-0 py-4 mx-6 border-t border-gray-200">
              {currentStep === 1 && (
                <div className="flex items-center justify-end gap-4">
                  <Button
                    variant="secondary"
                    size="md"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="md"
                    onClick={async () => {
                      if (!isStep1Complete()) return;
                      if (isAdhocDuplicateMode) {
                        if (duplicateSourcePlanId == null) {
                          showError("Duplicate failed", "Source plan is missing.");
                          return;
                        }
                        if (adhocCategoryId == null) {
                          warning("Category required", "Please select a category");
                          return;
                        }
                        setIsCreating(true);
                        try {
                          const cpfMinValue = `${step1Data.cpfRatio.min.c}:${step1Data.cpfRatio.min.p}:${step1Data.cpfRatio.min.f}`;
                          const cpfMaxValue = `${step1Data.cpfRatio.max.c}:${step1Data.cpfRatio.max.p}:${step1Data.cpfRatio.max.f}`;
                          const res = await duplicateAdhocMealPlan({
                            source_plan_id: duplicateSourcePlanId,
                            first_name: adhocContact.firstName,
                            last_name: adhocContact.lastName,
                            contact_number: adhocContact.phoneNumber || "",
                            client_email: "",
                            location: adhocContact.location,
                            category_id: adhocCategoryId,
                            plan_name: adhocPlanName,
                            meal_plan_type: step1Data.planType,
                            plan_goal: "",
                            total_calories_min: step1Data.totalCalories.min,
                            total_calories_max: step1Data.totalCalories.max,
                            total_protein_min: step1Data.totalAdjProtein.min,
                            total_protein_max: step1Data.totalAdjProtein.max,
                            total_carbs_min: step1Data.totalNetCarbs.min,
                            total_carbs_max: step1Data.totalNetCarbs.max,
                            total_fats_min: step1Data.totalFats.min,
                            total_fats_max: step1Data.totalFats.max,
                            total_GL_min: step1Data.totalGL.min,
                            total_GL_max: step1Data.totalGL.max,
                            CPF_min: cpfMinValue,
                            CPF_max: cpfMaxValue,
                            instructions: step1Data.instructions || "",
                          });
                          const ok =
                            res?.success === true ||
                            res?.success === "1" ||
                            res?.success === 1;
                          const newPlanId = res?.data?.adhoc_plan_id;
                          if (!ok || !newPlanId) {
                            showError(
                              "Duplicate failed",
                              res?.message || "Could not duplicate the plan.",
                            );
                            return;
                          }
                          navigate(`/adhoc-meal/${newPlanId}`, {
                            state: {
                              adhocMealMode: true,
                              showSuccessToast: true,
                            },
                          });
                        } catch (err) {
                          showError(
                            "Duplicate failed",
                            err instanceof Error ? err.message : "Could not duplicate the plan.",
                          );
                        } finally {
                          setIsCreating(false);
                        }
                        return;
                      }
                      setCurrentStep(isGlobalTemplateMode ? 3 : 2);
                    }}
                    disabled={!isStep1Complete() || (isAdhocDuplicateMode && isCreating)}
                  >
                    {isAdhocDuplicateMode
                      ? isCreating
                        ? "Duplicating..."
                        : "Save & Duplicate"
                      : "Save & continue"}
                  </Button>
                </div>
              )}

              {currentStep === 2 && (
                <div className="flex items-center justify-between w-full">
                  <button
                    onClick={() => setCurrentStep(1)}
                    className="flex items-center gap-2 text-[#0DB5B5] text-sm 3xl:text-base font-medium hover:text-[#0CA0A0] transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back
                  </button>
                  <div className="flex items-center gap-3">
                    <Button
                      variant="secondary"
                      size="md"
                      onClick={handleCancel}
                    >
                      Cancel
                    </Button>
                    {(() => {
                      const handleStep2Next = () => {
                        if (!step2Selection) {
                          showError("Please select an option to continue.");
                          return;
                        }
                        setCurrentStep(3);
                      };
                      return (
                        <Button
                          variant="primary"
                          size="md"
                          onClick={handleStep2Next}
                          disabled={!step2Selection}
                        >
                          Next
                        </Button>
                      );
                    })()}
                  </div>
                </div>
              )}

              {currentStep === 3 && (
                <div className="flex items-center justify-between w-full">
                  <button
                    onClick={() => setCurrentStep(isGlobalTemplateMode ? 1 : 2)}
                    disabled={isCreating}
                    className="flex items-center gap-2 text-[#0DB5B5] text-sm 3xl:text-base font-medium hover:text-[#0CA0A0] transition-colors disabled:opacity-50"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back
                  </button>
                  <div className="flex items-center gap-3">
                    <Button
                      variant="secondary"
                      size="md"
                      onClick={handleCancel}
                      disabled={isCreating}
                    >
                      Cancel
                    </Button>
                    {step2Selection === "scratch" && (
                      <Button
                        variant="primary"
                        size="md"
                        onClick={handleCreate}
                        disabled={isCreating || mealTimings.length === 0}
                      >
                        {isCreating && (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        )}
                        {isCreating ? "Creating..." : "Create"}
                      </Button>
                    )}
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>

      {/* ── Plan preview modals (shared component) ──────────────────────────── */}
      <PlanPreviewModal
        plan={previewTemplate}
        actionLabel="Use this template"
        isCreating={isCreating}
        isLoadingPreview={isLoadingPreview}
        onClose={() => { setPreviewTemplate(null); setIsLoadingPreview(false); }}
        onUse={(tpl) => { setPreviewTemplate(null); handleDuplicateTemplate(tpl); }}
      />
      <PlanPreviewModal
        plan={previewClientPlan}
        actionLabel="Duplicate & use"
        isCreating={isCreating}
        isLoadingPreview={isLoadingClientPreview}
        onClose={() => { setPreviewClientPlan(null); setIsLoadingClientPreview(false); }}
        onUse={(p) => { setPreviewClientPlan(null); handleDuplicatePlan(p); }}
      />


    </div>
  );
};

export default MealPlanCreatePage;
